import { useState, useEffect, useRef, memo } from 'react';
import { Button, Input, message, Spin, Select, Tooltip, Modal, InputNumber, Slider, Switch, Radio, Card, Space, Divider, Popconfirm, Collapse, Dropdown, Menu, Alert, Tag, Typography } from 'antd';
import { useNavigate } from 'react-router-dom';
import {
  SendOutlined,
  WalletOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined,
  ApiOutlined,
  ThunderboltOutlined,
  CrownOutlined,
  PlusOutlined,
  MessageOutlined,
  CopyOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  CheckOutlined,
  CloseOutlined,
  PictureOutlined,
  AudioOutlined,
  VideoCameraOutlined,
  SoundOutlined,
  SearchOutlined,
  LoadingOutlined,
  DownloadOutlined,
  PlayCircleOutlined,
  InfoCircleOutlined,
  MoreOutlined,
  PushpinOutlined,
  UpOutlined,
  LeftOutlined,
  ArrowRightOutlined,
  SortAscendingOutlined,
  NodeExpandOutlined,
  BranchesOutlined,
  CloseCircleOutlined,
  BulbOutlined,
} from '@ant-design/icons';
import { userAPI, chatAPI, modelAPI, apiKeyAPI, multimodalAPI, presetAPI, userModelConfigAPI } from '../services/api';
import { t } from '../utils/i18n';
import { copyToClipboard } from '../utils/clipboard';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import 'katex/dist/katex.min.css';
const { Text } = Typography;

const { TextArea } = Input;

// 微信风格音频播放器组件
const WechatAudioPlayer = ({ audioUrl }) => {
  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(null);

  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handlePlay = () => {
    if (audioRef.current) {
      if (playing) {
        audioRef.current.pause();
        setPlaying(false);
      } else {
        audioRef.current.play();
        setPlaying(true);
      }
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
      if (audioRef.current.ended) {
        setPlaying(false);
      }
    }
  };

  // 估算音频时长（基于URL中的duration参数或默认）
  const estimatedDuration = duration || 5;

  return (
    <>
      <audio
        ref={audioRef}
        src={audioUrl}
        onTimeUpdate={handleTimeUpdate}
        onEnded={() => setPlaying(false)}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onLoadedMetadata={() => setDuration(audioRef.current?.duration || 0)}
      />
      <div
        onClick={handlePlay}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          cursor: 'pointer',
          padding: '8px 12px',
          background: playing ? '#07c160' : '#f7f7f7',
          borderRadius: 20,
          minWidth: 120,
          transition: 'all 0.2s',
        }}
      >
        {playing ? (
          <PauseIcon />
        ) : (
          <PlayIcon />
        )}
        {/* 音频波形条 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              style={{
                width: 3,
                height: playing ? (8 + Math.random() * 12) : 8,
                backgroundColor: playing ? '#fff' : '#999',
                borderRadius: 1,
                transition: 'height 0.1s',
              }}
            />
          ))}
        </div>
        <span style={{ fontSize: 12, color: playing ? '#fff' : '#666', marginLeft: 4 }}>
          {formatDuration(duration)}
        </span>
      </div>
    </>
  );
};

// 播放图标
const PlayIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="#07c160">
    <path d="M8 5v14l11-7z" />
  </svg>
);

// 暂停图标
const PauseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="#fff">
    <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
  </svg>
);

// 预处理 LaTeX 格式
const preprocessLatex = (content) => {
  if (!content) return content;
  return content
    .replace(/\\\[([\s\S]*?)\\\]/g, (_, p1) => `$$${p1}$$`)
    .replace(/\\\(([\s\S]*?)\\\)/g, (_, p1) => `$${p1}$`);
};

// 格式化时间
const formatTime = (timestamp) => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const seconds = date.getSeconds().toString().padStart(2, '0');
  return `${year}/${month}/${day} ${hours}:${minutes}:${seconds}`;
};

// 提取消息项组件
const MessageItem = memo(({ msg, currentModel, onCopy, onEdit, onDelete, onRetry, isEditing, editContent, setEditContent, onSaveEdit, onCancelEdit, onCheckVideoStatus }) => {
  const isUser = msg.role === 'user';
  const modelName = currentModel || t('ai_assistant');
  const [copied, setCopied] = useState(false);

  // 获取消息状态显示
  const getStatusIcon = () => {
    if (!isUser) return null;
    if (msg.status === 'sending') {
      return <LoadingOutlined style={{ fontSize: 12, color: '#999', marginLeft: 8 }} />;
    }
    if (msg.status === 'failed') {
      return <Tooltip title={t('retry')}><span style={{ fontSize: 12, color: '#ff4d4f', marginLeft: 8, cursor: 'pointer' }} onClick={onRetry}>⚠️</span></Tooltip>;
    }
    if (msg.status === 'sent' || msg.status === 'synced') {
      return <CheckOutlined style={{ fontSize: 12, color: '#52c41a', marginLeft: 8 }} />;
    }
    return null;
  };

  const handleCopy = async () => {
    await copyToClipboard(msg.content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    if (onCopy) onCopy();
  };

  return (
    <div className={`chat-message ${msg.role} ${msg.status === 'failed' ? 'message-failed' : ''}`}>
      <div className="message-header-row">
        {!isUser && (
          <div className="message-avatar assistant-avatar">
            <span>🤖</span>
          </div>
        )}
        {!isUser && (
          <div className="message-info">
            <span className="message-name">{modelName}</span>
            {msg.timestamp && (
              <span className="message-time">{formatTime(msg.timestamp)}</span>
            )}
          </div>
        )}
        {isUser && (
          <div className="message-header-user">
            {msg.timestamp && (
              <span className="message-time">{formatTime(msg.timestamp)} <span className="message-name-inline">{t('me')}</span></span>
            )}
            {getStatusIcon()}
          </div>
        )}
        {isUser && (
          <div className="message-avatar user-avatar">
            <img src={require('../utils/img/logo.png')} alt={t('user_avatar')} />
          </div>
        )}
      </div>
      <div className={`message-row ${msg.role}`}>
        {!isUser && <div className="avatar-placeholder"></div>}
        <div className="message-body">
          <div className="message-content">
            {isEditing ? (
              <div className="message-edit-area">
                <TextArea
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  autoSize={{ minRows: 2, maxRows: 6 }}
                />
                <div className="message-edit-actions">
                  <Button size="small" type="primary" icon={<CheckOutlined />} onClick={onSaveEdit}>{t('save') || 'Save'}</Button>
                  <Button size="small" icon={<CloseOutlined />} onClick={onCancelEdit}>{t('cancel') || 'Cancel'}</Button>
                </div>
              </div>
            ) : msg.content ? (
              msg.role === 'assistant' ? (
                <ReactMarkdown
                remarkPlugins={[remarkGfm, remarkMath]}
                rehypePlugins={[rehypeKatex]}
                components={{
                  h1: ({ children }) => <h1 style={{ fontSize: 20, marginBottom: 12, marginTop: 16 }}>{children}</h1>,
                  h2: ({ children }) => <h2 style={{ fontSize: 18, marginBottom: 10, marginTop: 14 }}>{children}</h2>,
                  h3: ({ children }) => <h3 style={{ fontSize: 16, marginBottom: 8, marginTop: 12 }}>{children}</h3>,
                  p: ({ children }) => <p style={{ marginBottom: 8, lineHeight: 1.6 }}>{children}</p>,
                  ul: ({ children }) => <ul style={{ marginLeft: 20, marginBottom: 8 }}>{children}</ul>,
                  ol: ({ children }) => <ol style={{ marginLeft: 20, marginBottom: 8 }}>{children}</ol>,
                  li: ({ children }) => <li style={{ marginBottom: 4, lineHeight: 1.5 }}>{children}</li>,
                  code: ({ inline, className, children, ...props }) => {
                    const match = /language-(\w+)/.exec(className || '');
                    const language = match ? match[1] : '';
                    const codeString = String(children).replace(/\n$/, '');

                    if (!inline && (language || codeString.includes('\n'))) {
                      return (
                        <SyntaxHighlighter
                          style={oneLight}
                          language={language || 'text'}
                          PreTag="div"
                          customStyle={{
                            margin: '0 0 8px 0',
                            borderRadius: 6,
                            fontSize: 13,
                          }}
                          {...props}
                        >
                          {codeString}
                        </SyntaxHighlighter>
                      );
                    }

                    return (
                      <code style={{ background: '#f5f5f5', padding: '2px 6px', borderRadius: 4, fontSize: 13, color: '#d63384' }} {...props}>
                        {children}
                      </code>
                    );
                  },
                  pre: ({ children }) => <div style={{ marginBottom: 8 }}>{children}</div>,
                  blockquote: ({ children }) => <blockquote style={{ borderLeft: '3px solid #1677ff', paddingLeft: 12, marginLeft: 0, color: '#666', marginBottom: 8 }}>{children}</blockquote>,
                  strong: ({ children }) => <strong style={{ fontWeight: 600 }}>{children}</strong>,
                  hr: () => <hr style={{ border: 'none', borderTop: '1px solid #e5e7eb', margin: '16px 0' }} />,
                  table: ({ children }) => <div style={{ overflowX: 'auto', marginBottom: 8 }}><table style={{ borderCollapse: 'collapse', width: '100%' }}>{children}</table></div>,
                  th: ({ children }) => <th style={{ border: '1px solid #e5e7eb', padding: '8px 12px', background: '#f9fafb', textAlign: 'left' }}>{children}</th>,
                  td: ({ children }) => <td style={{ border: '1px solid #e5e7eb', padding: '8px 12px' }}>{children}</td>,
                  a: ({ href, children }) => <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: '#1677ff' }}>{children}</a>,
                }}
              >
                {              preprocessLatex(msg.content)}
              </ReactMarkdown>
            ) : (
              <span style={{ whiteSpace: 'pre-wrap' }}>{msg.content}</span>
            )
          ) : msg.isLoading ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0' }}>
              <LoadingOutlined style={{ fontSize: 16, color: '#1677ff' }} />
              <span style={{ color: '#666', fontSize: 13 }}>
                {msg.videoTaskId ? (t('video_checking_status') || '检查视频状态中...') : 
                 msg.images ? (t('image_generating') || '图片生成中...') :
                 msg.audioUrl !== undefined ? (t('voice_synthesizing') || '语音合成中...') :
                 msg.embedding ? (t('embedding_generating') || '向量生成中...') :
                 (t('generating') || '生成中...')}
              </span>
              <span style={{
                display: 'inline-block',
                width: 6,
                height: 6,
                borderRadius: '50%',
                background: '#1677ff',
                marginLeft: 4,
                animation: 'pulse 1.5s ease-in-out infinite'
              }} />
            </div>
          ) : (
            <Spin size="small" />
          )}
          </div>
          
          {/* 图片预览 */}
          {msg.images && msg.images.length > 0 && (
            <div style={{ marginTop: 12, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {msg.images.map((img, idx) => (
                <div key={idx} style={{ position: 'relative' }}>
                  {img.startsWith('data:') ? (
                    <img 
                      src={img} 
                      alt={`${t('generated_image')} ${idx + 1}`} 
                      style={{ maxWidth: 300, maxHeight: 300, borderRadius: 8 }}
                      onLoad={() => requestAnimationFrame(() => {
                        const scrollContainer = document.querySelector('.messages-scroll-container');
                        if (scrollContainer) {
                          scrollContainer.scrollTop = scrollContainer.scrollHeight;
                        }
                      })}
                    />
                  ) : (
                    <img 
                      src={img} 
                      alt={`${t('generated_image')} ${idx + 1}`} 
                      style={{ maxWidth: 300, maxHeight: 300, borderRadius: 8 }}
                      onClick={() => window.open(img, '_blank')}
                      onLoad={() => requestAnimationFrame(() => {
                        const scrollContainer = document.querySelector('.messages-scroll-container');
                        if (scrollContainer) {
                          scrollContainer.scrollTop = scrollContainer.scrollHeight;
                        }
                      })}
                    />
                  )}
                  <Button 
                    size="small" 
                    icon={<DownloadOutlined />} 
                    style={{ position: 'absolute', top: 4, right: 4 }}
                    onClick={() => window.open(img, '_blank')}
                  />
                </div>
              ))}
            </div>
          )}

          {/* 音频播放 - 微信语音风格 */}
          {msg.audioUrl && (
            <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
              <WechatAudioPlayer audioUrl={msg.audioUrl} />
            </div>
          )}
          
          {/* 视频播放 */}
          {msg.videoUrl && (
            <div style={{ marginTop: 12 }}>
              <video controls style={{ width: '100%', maxWidth: 500, borderRadius: 8 }}>
                <source src={msg.videoUrl} type="video/mp4" />
                {t('video_browser_unsupported')}
              </video>
            </div>
          )}
          
          {/* 视频任务状态查询 - 可折叠区域 */}
          {msg.videoTaskId && !msg.videoUrl && (
            <Collapse 
              ghost 
              size="small"
              items={[{
                key: 'video-status',
                label: (
                  <span style={{ fontSize: 12, color: '#1890ff' }}>
                    <VideoCameraOutlined /> {t('video_generating')}
                  </span>
                ),
                children: (
                  <div style={{ 
                    padding: '8px 12px', 
                    background: '#f5f5f5', 
                    borderRadius: 6,
                    maxHeight: 120,
                    overflow: 'auto'
                  }}>
                    <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>
                      <InfoCircleOutlined /> {t('task_id')}: {msg.videoTaskId}
                    </div>
                    <Button 
                      type="primary" 
                      icon={<SearchOutlined />} 
                      size="small"
                      onClick={() => onCheckVideoStatus?.(msg.videoTaskId)}
                      style={{ borderRadius: 4 }}
                    >
                      {t('refresh_status')}
                    </Button>
                  </div>
                ),
              }]}
            />
          )}
          {/* 操作按钮 */}
          <div className="message-actions">
            <Tooltip title={copied ? (t('copied') || 'Copied') : (t('copy') || 'Copy')}>
              <button className="message-action-btn" onClick={handleCopy}>
                {copied ? <CheckOutlined /> : <CopyOutlined />}
              </button>
            </Tooltip>
            {isUser && (
              <Tooltip title={t('edit') || 'Edit'}>
                <button className="message-action-btn" onClick={onEdit}>
                  <EditOutlined />
                </button>
              </Tooltip>
            )}
            {!isUser && (
              <Tooltip title={t('regenerate') || 'Regenerate'}>
                <button className="message-action-btn" onClick={onRetry}>
                  <ReloadOutlined />
                </button>
              </Tooltip>
            )}
            <Tooltip title={t('delete') || 'Delete'}>
              <button className="message-action-btn" onClick={onDelete}>
                <DeleteOutlined />
              </button>
            </Tooltip>
          </div>
        </div>
        {isUser && <div className="avatar-placeholder"></div>}
      </div>
    </div>
  );
});

const Dashboard = () => {
  const navigate = useNavigate();
  const [userInfo, setUserInfo] = useState(null);
  const [balance, setBalance] = useState(0);
  const [loading, setLoading] = useState(true);

  // 隐藏浏览器滚动条
  useEffect(() => {
    document.body.classList.add('hide-scrollbar');
    return () => {
      document.body.classList.remove('hide-scrollbar');
    };
  }, []);
  const [chatLoading, setChatLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState('');
  const [selectedProvider, setSelectedProvider] = useState('all');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [showEditConfirm, setShowEditConfirm] = useState(false);
  const [pendingEditIndex, setPendingEditIndex] = useState(null);
  const messagesEndRef = useRef(null);
  const streamContentRef = useRef('');
  const abortControllerRef = useRef(null);
  const selectedModelRef = useRef('');

  // 用户引导弹窗状态 - 首次登录显示指引
  const [showGuideModal, setShowGuideModal] = useState(() => {
    // 只在首次登录时显示引导
    const hasSeenGuide = localStorage.getItem('has_seen_user_guide');
    return !hasSeenGuide;
  });

  // 多模态相关状态
  const [modeType, setModeType] = useState('text'); // text, image, embeddings, audio, video, rerank, speech, transcription
  const [multimodalLoading, setMultimodalLoading] = useState(false);
  const [multimodalResult, setMultimodalResult] = useState(null);
  
  // 固定类型映射
  const TYPE_MAPPING = {
    text: { en: 'Text', cn: '对话', icon: 'chat' },
    image: { en: 'Image', cn: '图像', icon: 'image' },
    embeddings: { en: 'Embeddings', cn: '嵌入', icon: 'embeddings' },
    audio: { en: 'Audio', cn: '语音', icon: 'audio' },
    video: { en: 'Video', cn: '视频', icon: 'video' },
    rerank: { en: 'Rerank', cn: '重排序', icon: 'rerank' },
    speech: { en: 'Speech', cn: '语音合成', icon: 'speech' },
    transcription: { en: 'Transcription', cn: '语音识别', icon: 'transcription' },
  };
  const FIXED_TYPES = Object.keys(TYPE_MAPPING);
  
  // 可用类型列表
  const [availableTypes, setAvailableTypes] = useState(FIXED_TYPES);

  // 对话列表状态（微信风格）
  const [conversations, setConversations] = useState([]);
  const [currentConversationId, setCurrentConversationId] = useState(null);
  const [conversationTitle, setConversationTitle] = useState('');
  const [showConversationList, setShowConversationList] = useState(false); // 控制对话列表显示
  const [renamingConvId, setRenamingConvId] = useState(null); // 当前重命名的会话ID
  const [renameValue, setRenameValue] = useState(''); // 重命名输入值
  const [conversationSearch, setConversationSearch] = useState(''); // 对话搜索
  const [conversationClosing, setConversationClosing] = useState(false); // 对话列表收起动画
  
  // 图片生成参数
  const [imageCount, setImageCount] = useState(1);
  const [imageSize, setImageSize] = useState('1024x1024');
  const [imageGuidance, setImageGuidance] = useState(7.5);
  const [imageSeed, setImageSeed] = useState(null);
  const [useRandomImageSeed, setUseRandomImageSeed] = useState(true);
  
  // 语音合成参数
  const [voiceId, setVoiceId] = useState('BV001');
  const [speechSpeed, setSpeechSpeed] = useState(1.0);
  
  // 视频生成参数
  const [videoDuration, setVideoDuration] = useState(5);
  const [videoResolution, setVideoResolution] = useState('720p');
  const [videoAspectRatio, setVideoAspectRatio] = useState('16:9');
  const [videoSeed, setVideoSeed] = useState(null);
  const [useRandomVideoSeed, setUseRandomVideoSeed] = useState(true);
  const [videoWatermark, setVideoWatermark] = useState(false);
  
  // API Key 管理
  const [apiKeys, setApiKeys] = useState([]);
  const [selectedApiKey, setSelectedApiKey] = useState(null);
  const [noApiKey, setNoApiKey] = useState(false);
  const userSelectedApiKeyRef = useRef(false); // 标记用户是否手动选择过key
  const selectedProviderRef = useRef(selectedProvider); // 保存当前供应商引用

  // 用户训练的模型参数
  const [userTrainedConfig, setUserTrainedConfig] = useState(null); // 当前使用的训练配置
  const [userTrainedParams, setUserTrainedParams] = useState(null); // 训练配置中的参数
  const [userTrainedBoundModel, setUserTrainedBoundModel] = useState(null); // 训练配置绑定的模型
  const [currentPresetId, setCurrentPresetId] = useState(null); // 当前使用的预设ID
  const [currentPresetName, setCurrentPresetName] = useState(''); // 当前使用的预设名称
  const [currentConfigId, setCurrentConfigId] = useState(null); // 当前使用的训练配置ID
  const [presetBoundModel, setPresetBoundModel] = useState(null); // 预设绑定的模型
  const [presetPrompt, setPresetPrompt] = useState(''); // 预设的提示词（不会被清空）

  // 数据流侧边栏
  const [dataFlowVisible, setDataFlowVisible] = useState(false);
  const [requestData, setRequestData] = useState(null);
  const [responseData, setResponseData] = useState(null);
  const [currentUsage, setCurrentUsage] = useState(null); // 当前请求的用量

  // 同步 ref
  useEffect(() => { selectedProviderRef.current = selectedProvider; }, [selectedProvider]);
  useEffect(() => { selectedModelRef.current = selectedModel; }, [selectedModel]);

  // 提取版本号用于排序（返回数字，越大约版本越新）
  const extractVersion = (modelName) => {
    if (!modelName) return 0;
    // 匹配主版本号，如 glm-5.1, glm-4, claude-3-5, gpt-4
    const mainMatch = modelName.match(/(\d+)/);
    if (mainMatch) {
      const major = parseInt(mainMatch[1]) || 0;
      // 尝试匹配次版本号，如 5.1 中的 1
      const minorMatch = modelName.match(/\.(\d+)/) || modelName.match(/-(\d+)-/);
      const minor = minorMatch ? parseInt(minorMatch[1]) : 0;
      // 返回组合版本号，如 5.1 -> 501, 4 -> 400, 3.5 -> 305
      return major * 100 + minor;
    }
    return 0;
  };

  // 根据模型名获取供应商（优先从 models 列表查找）
  const getProviderFromModel = (modelName) => {
    if (!modelName || models.length === 0) return null;
    // 从 models 列表中查找模型的供应商
    const model = models.find(m => m.model_name === modelName);
    if (model && model.provider) {
      if (typeof model.provider === 'object') {
        return model.provider.display_name || model.provider.id;
      }
      return model.provider;
    }
    // 后备：根据模型名前缀推断（覆盖主流模型）
    const lower = modelName.toLowerCase();
    if (lower.startsWith('glm-')) return 'zhipu';
    if (lower.startsWith('gpt-') || lower.startsWith('o1') || lower.startsWith('o3') || lower.startsWith('chatgpt')) return 'openai';
    if (lower.startsWith('claude-') || lower.startsWith('sonnet') || lower.startsWith('opus')) return 'anthropic';
    if (lower.startsWith('ernie-')) return 'baidu';
    if (lower.startsWith('abab')) return 'minimax';
    if (lower.startsWith('qwen-') || lower.startsWith('tongyi')) return 'ali';
    if (lower.startsWith('deepseek-')) return 'deepseek';
    if (lower.startsWith('moonshot-')) return 'moonshot';
    if (lower.startsWith('yi-')) return 'lingyi';
    return null;
  };

  // 获取类型显示名称（根据当前语言）
  const getTypeDisplayName = (type) => {
    const mapping = TYPE_MAPPING[type];
    if (mapping) {
      const lang = localStorage.getItem('lang') || 'en';
      return lang === 'en' ? mapping.en : mapping.cn;
    }
    return type;
  };

  // 获取类型图标
  const getTypeIcon = (type) => {
    const mapping = TYPE_MAPPING[type];
    if (mapping) {
      switch (mapping.icon) {
        case 'chat': return <MessageOutlined />;
        case 'image': return <PictureOutlined />;
        case 'audio': return <AudioOutlined />;
        case 'video': return <VideoCameraOutlined />;
        case 'embeddings': return <SearchOutlined />;
        case 'rerank': return <SortAscendingOutlined />;
        case 'speech': return <SoundOutlined />;
        case 'transcription': return <AudioOutlined />;
        default: return <ApiOutlined />;
      }
    }
    return <ApiOutlined />;
  };
  
  // 获取某类型模型数量
  const getTypeModelCount = (type) => {
    return models.filter(m => m.type === type).length;
  };

  // 获取唯一厂家列表（根据当前模式类型过滤）
  const getFilteredProviders = () => {
    // 按模式类型过滤
    const filteredByType = models.filter(m => m.type === modeType);
    
    const providerMap = new Map();
    filteredByType.forEach(m => {
      if (m.provider) {
        if (typeof m.provider === 'object' && m.provider.display_name) {
          providerMap.set(m.provider.display_name, m.provider.display_name);
        } else if (typeof m.provider === 'string') {
          providerMap.set(m.provider, m.provider);
        }
      }
    });
    return Array.from(providerMap.values()).sort();
  };

  // 获取过滤并排序的模型列表
  const getFilteredModels = () => {
    let filtered = models;
    
    // 按模式类型过滤
    filtered = filtered.filter(m => m.type === modeType);
    
    // 按厂商过滤
    if (selectedProvider !== 'all') {
      filtered = filtered.filter(m => {
        // provider 可能是对象或字符串
        const providerName = typeof m.provider === 'object' ? m.provider?.display_name : m.provider;
        return providerName === selectedProvider;
      });
    }
    
    // 全部厂商按名称字母顺序排序
    if (selectedProvider === 'all') {
      return [...filtered].sort((a, b) => 
        (a.model_name || '').localeCompare(b.model_name || '')
      );
    }
    
    // 特定厂商按版本号从大到小排序（新的在前）
    return [...filtered].sort((a, b) => {
      const versionA = extractVersion(a.model_name);
      const versionB = extractVersion(b.model_name);
      if (versionB !== versionA) return versionB - versionA;
      // 版本相同按字母顺序
      return (a.model_name || '').localeCompare(b.model_name || '');
    });
  };

  // 根据当前筛选条件获取可用的供应商列表
  const filteredProviders = getFilteredProviders();
  const filteredModels = getFilteredModels();

  // 建议标签 - 使用 i18n 翻译
  const suggestionTags = [
    t('suggestion_spring'),
    t('suggestion_quantum'),
    t('suggestion_python'),
  ];

  useEffect(() => {
    fetchUserInfo();
    fetchBalance();
    fetchModels();
    fetchApiKeys();
    loadConversations(); // 加载对话列表
  }, []);

  // 从URL参数加载预设
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const presetId = params.get('preset_id');
    if (presetId) {
      presetAPI.getPreset(presetId).then(res => {
        // 检查响应结构：可能是 {code, message, data} 或直接是预设对象
        const preset = res.data?.code === 0 ? res.data.data : (res.data?.id ? res.data : null);
        if (preset) {
          // 预设内容只作为 system 消息，不设置到 inputValue
          setPresetPrompt(preset.prompt || '');
          setCurrentPresetName(preset.name || ''); // 保存预设名称
          if (preset.model) {
            setSelectedModel(preset.model);
            setPresetBoundModel(preset.model); // 保存预设绑定的模型
          }
          // 保存预设ID，用于发送消息时增加使用量
          setCurrentPresetId(presetId);
          // 清除URL参数
          params.delete('preset_id');
          const newUrl = window.location.pathname + (params.toString() ? '?' + params.toString() : '');
          window.history.replaceState({}, '', newUrl);
        }
      }).catch(err => {
        console.error('加载预设失败:', err);
      });
    }
  }, []);

  // 清除预设
  const clearPreset = () => {
    setPresetPrompt('');
    setCurrentPresetId(null);
    setCurrentPresetName('');
    setPresetBoundModel(null);
  };

  // 从 localStorage 加载用户训练的模型参数
  useEffect(() => {
    // 同时支持两种 key：playground_trained_config 和 selected_trained_model
    const savedConfig = localStorage.getItem('playground_trained_config') || localStorage.getItem('selected_trained_model');
    if (savedConfig) {
      try {
        const config = JSON.parse(savedConfig);
        // 解析 parameters（可能是字符串或对象）
        const configParams = typeof config.parameters === 'string' 
          ? JSON.parse(config.parameters) 
          : (config.parameters || {});
        
        setUserTrainedConfig(config);
        setUserTrainedParams(configParams);
        // 保存训练配置绑定的模型
        setUserTrainedBoundModel(config.modelName || config.model_name || null);
        
        // 保存训练配置ID，用于发送消息时增加使用量
        if (config.configId) {
          setCurrentConfigId(config.configId);
        }
        
        // 如果有 system_prompt，设置到 presetPrompt（用于发送时作为 system message）
        if (configParams.system_prompt) {
          setPresetPrompt(configParams.system_prompt);
          // 如果有预设名称，使用预设名称；否则使用配置名称
          setCurrentPresetName(config.presetName || config.configName || config.name || '训练配置');
          // 如果有预设ID，保存预设ID用于增加使用量
          if (config.presetId) {
            setCurrentPresetId(config.presetId);
          }
          // 训练配置也设置 presetBoundModel，让标签显示状态一致
          setPresetBoundModel(config.modelName || config.model_name || null);
        }
        // 如果有绑定的模型，则设置模型
        if (config.modelName || config.model_name) {
          setSelectedModel(config.modelName || config.model_name);
        }

        // 清除 localStorage 中的配置（只加载一次）
        localStorage.removeItem('playground_trained_config');
        localStorage.removeItem('selected_trained_model');
      } catch (e) {
        console.error('加载训练配置失败:', e);
        localStorage.removeItem('playground_trained_config');
        localStorage.removeItem('selected_trained_model');
      }
    }
  }, []);

  // ========== 对话列表管理（微信风格） ==========
  
  // 生成对话标题
  const generateTitle = (msgs) => {
    if (!msgs || msgs.length === 0) return t('new_chat');
    const firstUserMsg = msgs.find(m => m.role === 'user');
    if (firstUserMsg) {
      const content = firstUserMsg.content || '';
      return content.length > 20 ? content.substring(0, 20) + '...' : content;
    }
    return t('new_chat');
  };

  // 加载对话列表
  const loadConversations = () => {
    try {
      const saved = localStorage.getItem('conversations');
      if (saved) {
        const list = JSON.parse(saved);
        setConversations(list);
      }
    } catch (e) {
      console.error('加载对话列表失败:', e);
    }
  };

  // 保存对话列表
  const saveConversations = (list) => {
    try {
      localStorage.setItem('conversations', JSON.stringify(list));
    } catch (e) {
      console.error('保存对话列表失败:', e);
    }
  };

  // 创建新对话
  const createConversation = () => {
    const newConv = {
      id: Date.now().toString(),
      title: t('new_chat'),
      messages: [],
      modeType: 'text',
      createdAt: Date.now(),
      updatedAt: Date.now(),
    };
    const newList = [newConv, ...conversations];
    setConversations(newList);
    setCurrentConversationId(newConv.id);
    setMessages([]);
    setInputValue('');
    setConversationTitle('');
    saveConversations(newList);
    return newConv;
  };

  // 保存当前对话
  const saveCurrentConversation = (updatedMessages, forceTitle = false) => {
    // 如果选的是 all，从模型名推断供应商
    const provider = selectedProviderRef.current === 'all' 
      ? getProviderFromModel(selectedModelRef.current) 
      : selectedProviderRef.current;
    
    // 检查是否有有效回复（assistant 消息且有内容）
    const hasValidResponse = updatedMessages.some(m => 
      m.role === 'assistant' && 
      m.content && 
      !m.content.includes('请求已取消') && 
      !m.content.includes('请求超时') && 
      !m.content.includes('请求失败') &&
      !m.content.includes('⚠️')
    );
    
    // 如果没有当前对话，先创建一个
    if (!currentConversationId) {
      const newConv = {
        id: Date.now().toString(),
        // 只有当有有效回复时才生成标题，否则用默认标题
        title: hasValidResponse ? generateTitle(updatedMessages) : t('new_chat'),
        messages: updatedMessages,
        modeType: modeType,
        provider: provider,
        model: selectedModelRef.current,
        createdAt: Date.now(),
        updatedAt: Date.now(),
      };
      const newList = [newConv, ...conversations];
      setConversations(newList);
      setCurrentConversationId(newConv.id);
      saveConversations(newList);
      return;
    }
    
    const updatedList = conversations.map(conv => {
      if (conv.id === currentConversationId) {
        return {
          ...conv,
          messages: updatedMessages,
          // 只有当有有效回复且标题为默认标题时才生成
          title: (conv.title === '新对话' || conv.title === t('new_chat') || forceTitle) && hasValidResponse 
            ? generateTitle(updatedMessages) 
            : conv.title,
          modeType: modeType,
          provider: provider,
          model: selectedModelRef.current,
          updatedAt: Date.now(),
        };
      }
      return conv;
    });
    
    setConversations(updatedList);
    saveConversations(updatedList);
  };

  // 切换对话
  const switchConversation = (convId) => {
    setConversations(prev => {
      const conv = prev.find(c => c.id === convId);
      if (conv) {
        setCurrentConversationId(convId);
        const loadedMessages = conv.messages || [];
        setMessages(loadedMessages);
        setModeType(conv.modeType || 'text');
        setConversationTitle(conv.title);
        if (conv.provider) setSelectedProvider(conv.provider);
        if (conv.model) setSelectedModel(conv.model);
        
        // 切换对话时清除预设状态
        setPresetPrompt('');
        setCurrentPresetId(null);
        setCurrentPresetName('');
        setPresetBoundModel(null);
        
        // 延迟滚动到底部，确保消息已渲染
        setTimeout(() => scrollToBottom(), 50);
        
        // 自动刷新多媒体任务状态
        setTimeout(() => {
          // 查找所有有待查询的视频任务（有videoTaskId但没有videoUrl）
          const videoTasks = loadedMessages.filter(m => m.videoTaskId && !m.videoUrl);
          if (videoTasks.length > 0) {
            videoTasks.forEach((task, idx) => {
              setTimeout(() => handleCheckVideoStatus(task.videoTaskId), idx * 500);
            });
          }
          
          // 如果有正在加载的多媒体任务，也触发检测
          const loadingTasks = loadedMessages.filter(m => m.isLoading);
          if (loadingTasks.length > 0) {
            loadingTasks.forEach((task, idx) => {
              if (task.videoTaskId) {
                setTimeout(() => handleCheckVideoStatus(task.videoTaskId), idx * 500);
              }
            });
          }
        }, 300);
      }
      return prev;
    });
  };

  // 删除对话
  const deleteConversation = (convId, e) => {
    e?.stopPropagation();
    const newList = conversations.filter(c => c.id !== convId);
    setConversations(newList);
    saveConversations(newList);
    message.success(t('conversation_deleted'));
    
    if (currentConversationId === convId) {
      if (newList.length > 0) {
        switchConversation(newList[0].id);
      } else {
        createConversation();
      }
    }
  };

  // 更新对话标题
  const updateConversationTitle = (convId, newTitle) => {
    const updatedList = conversations.map(conv => {
      if (conv.id === convId) {
        return { ...conv, title: newTitle };
      }
      return conv;
    });
    setConversations(updatedList);
    saveConversations(updatedList);
  };

  // 置顶对话
  const pinConversation = (convId) => {
    const conv = conversations.find(c => c.id === convId);
    if (conv) {
      const newList = conversations.filter(c => c.id !== convId);
      const pinnedConv = { ...conv, isPinned: true, updatedAt: Date.now() };
      newList.unshift(pinnedConv);
      setConversations(newList);
      saveConversations(newList);
      message.success(t('pinned'));
    }
  };

  // 开始重命名
  const startRename = (conv) => {
    setRenamingConvId(conv.id);
    setRenameValue(conv.title);
  };

  // 完成重命名
  const finishRename = () => {
    if (renamingConvId && renameValue.trim()) {
      updateConversationTitle(renamingConvId, renameValue.trim());
    }
    setRenamingConvId(null);
    setRenameValue('');
  };

  // 消息变化时自动保存
  useEffect(() => {
    // 聊天消息需要有效内容才保存，但多媒体消息（isLoading或有特定字段）也要保存
    const hasValidResponse = messages.some(m => 
      m.role === 'assistant' && 
      (
        // 有内容且不是错误消息
        (m.content && !m.content.includes('请求已取消') && !m.content.includes('请求超时') && !m.content.includes('请求失败')) ||
        // 或者正在加载的多媒体任务
        m.isLoading ||
        // 或者有图片
        (m.images && m.images.length > 0) ||
        // 或者有音频
        m.audioUrl ||
        // 或者有视频任务
        m.videoTaskId ||
        // 或者有向量
        m.embedding
      )
    );
    
    if (messages.length > 0 && currentConversationId && hasValidResponse) {
      saveCurrentConversation(messages);
    }
  }, [messages, currentConversationId]);

  // 轮询更新 API Keys 额度（每 5 秒）
  useEffect(() => {
    const interval = setInterval(() => {
      fetchApiKeys();
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // 自动检查视频任务状态的轮询（每 10 秒）
  useEffect(() => {
    const checkVideoTasks = () => {
      const videoTasks = messages.filter(m => m.videoTaskId && !m.videoUrl);
      if (videoTasks.length > 0) {
        const task = videoTasks[0];
        handleCheckVideoStatus(task.videoTaskId);
      }
    };
    
    const interval = setInterval(checkVideoTasks, 10000);
    return () => clearInterval(interval);
  }, [messages]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    // 使用 requestAnimationFrame 确保 DOM 更新后再滚动
    requestAnimationFrame(() => {
      const scrollContainer = document.querySelector('.messages-scroll-container');
      if (scrollContainer) {
        scrollContainer.scrollTop = scrollContainer.scrollHeight;
      }
    });
    // 再次滚动以处理图片等异步加载的内容
    setTimeout(() => {
      requestAnimationFrame(() => {
        const scrollContainer = document.querySelector('.messages-scroll-container');
        if (scrollContainer) {
          scrollContainer.scrollTop = scrollContainer.scrollHeight;
        }
      });
    }, 100);
  };

  const fetchUserInfo = async () => {
    try {
      const response = await userAPI.getInfo();
      if (response.code === 0) {
        setUserInfo(response.data);
      }
    } catch (error) {
      console.error('获取用户信息失败:', error);
    }
  };

  const fetchBalance = async () => {
    try {
      const response = await userAPI.getQuota();
      if (response.code === 0) {
        setBalance(parseFloat(response.data.balance || 0));
      }
    } catch (error) {
      console.error('获取余额失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchModels = async () => {
    try {
      const response = await modelAPI.getAvailable();
      if (response.code === 0 && response.data?.list) {
        setModels(response.data.list);
        
        // 使用固定类型列表
        setAvailableTypes(FIXED_TYPES);
        
        if (response.data.list.length > 0 && !selectedModelRef.current) {
          const defaultModel = response.data.list[0].model_name;
          setSelectedModel(defaultModel);
          selectedModelRef.current = defaultModel;
        }
      }
    } catch (error) {
      console.error('获取模型列表失败:', error);
    }
  };

  // 获取特定类型的模型
  const getModelsByType = (type) => {
    return models.filter(m => m.type === type);
  };

  // 获取多模态模型的默认模型（从已加载的模型列表中获取）
  const getDefaultMultimodalModel = (type) => {
    const typeModels = getModelsByType(type);
    if (typeModels.length > 0) {
      return typeModels[0].model_name;
    }
    return '';
  };

  // 处理模式切换
  const handleModeTypeChange = (newType) => {
    // 图片/视频生成跳转到灵感创作页面
    if (newType === 'image' || newType === 'video') {
      navigate('/dashboard/creative');
      return;
    }
    // 语音合成/识别跳转到语音服务页面
    if (newType === 'speech' || newType === 'transcription') {
      navigate('/dashboard/audio');
      return;
    }
    // 切换模式时始终重置供应商为 all，避免保留之前的供应商选择
    setSelectedProvider('all');
    selectedProviderRef.current = 'all';

    // 设置新模式
    setModeType(newType);
    setMultimodalResult(null);
    setInputValue('');

    // 根据新模式获取可用模型
    let typeModels = models;
    if (newType) {
      typeModels = models.filter(m => m.type === newType);
    } else {
      typeModels = models.filter(m => m.type === 'text');
    }

    // 排序并选择第一个模型
    if (typeModels.length > 0) {
      const sortedModels = [...typeModels].sort((a, b) =>
        (a.model_name || '').localeCompare(b.model_name || '')
      );
      setSelectedModel(sortedModels[0].model_name);
      selectedModelRef.current = sortedModels[0].model_name;
    } else {
      setSelectedModel('');
      selectedModelRef.current = '';
    }
  };

  // 处理图片生成
  const handleGenerateImage = async () => {
    if (!inputValue.trim()) {
      message.warning(t('enter_prompt') || '请输入提示词');
      return;
    }
    
    const userContent = inputValue;
    setInputValue(''); // 先清空输入框
    setMultimodalLoading(true);
    setMultimodalResult(null);
    
    // 先添加用户消息
    const userMsg = { role: 'user', content: userContent, timestamp: Date.now() };
    setMessages(prev => [...prev, userMsg]);
    
    // 添加"生成中"状态的消息
    setMessages(prev => [...prev, { 
      role: 'assistant', 
      content: `🖼️ ${t('image_generating') || '图片生成中...'}\n\n${t('prompt')}: ${userContent}\n${t('model')}: ${selectedModel || 'cogview-3'}`,
      isLoading: true, 
      timestamp: Date.now() 
    }]);
    const assistantIndex = messages.length + 1;
    scrollToBottom();
    
    // 立即隐藏加载状态，用户可以做其他事情
    setMultimodalLoading(false);
    
    try {
      const reqBody = {
        model: selectedModel || 'cogview-3',
        prompt: userContent,
        n: imageCount,
        size: imageSize,
        guidance_scale: imageGuidance,
      };
      if (!useRandomImageSeed && imageSeed != null) reqBody.seed = parseInt(imageSeed);

      const response = await multimodalAPI.generateImage(reqBody);
      
      // 处理响应 - 支持两种错误格式
      if (response.error) {
        setMessages(prev => prev.map((m, i) => i === assistantIndex ? {
          role: 'assistant',
          content: `❌ ${t('image_generation_failed')}\n\n${t('prompt')}: ${userContent}\n${t('error_occurred')}: ${response.error.message || t('try_later')}`,
          isError: true,
          isLoading: false,
          timestamp: Date.now(),
        } : m));
        return;
      } else if (response.code && response.code !== 0) {
        setMessages(prev => prev.map((m, i) => i === assistantIndex ? {
          role: 'assistant',
          content: `❌ ${t('image_generation_failed')}\n\n${t('prompt')}: ${userContent}\n${t('error_occurred')}: ${response.message || t('try_later')}`,
          isError: true,
          isLoading: false,
          timestamp: Date.now(),
        } : m));
        return;
      }
      
      setMultimodalResult({ type: 'image', data: response });
      // 更新助手消息
      setMessages(prev => prev.map((m, i) => i === assistantIndex ? {
        role: 'assistant',
        content: `✅ ${t('image_generation_success')}\n\n${t('prompt')}: ${userContent}\n${t('model')}: ${selectedModel || 'cogview-3'}`,
        images: response.data?.map(d => d.url || d.b64_json),
        isLoading: false,
        timestamp: Date.now(),
      } : m));
      scrollToBottom();
    } catch (error) {
      setMessages(prev => prev.map((m, i) => i === assistantIndex ? {
        role: 'assistant',
        content: `❌ ${t('image_generation_failed')}\n\n${t('prompt')}: ${userContent}\n${t('error_occurred')}: ${error.message || t('try_later')}`,
        isError: true,
        isLoading: false,
        timestamp: Date.now(),
      } : m));
      scrollToBottom();
    }
    fetchBalance();
  };

  // 处理语音合成
  const handleTextToSpeech = async () => {
    if (!inputValue.trim()) {
      message.warning(t('enter_text') || '请输入要转换的文本');
      return;
    }
    
    const userContent = inputValue;
    setInputValue(''); // 先清空输入框
    setMultimodalLoading(true);
    setMultimodalResult(null);
    
    // 先添加用户消息
    const userMsg = { role: 'user', content: userContent, timestamp: Date.now() };
    setMessages(prev => [...prev, userMsg]);
    
    // 添加"生成中"状态的消息
    setMessages(prev => [...prev, { 
      role: 'assistant', 
      content: `🔊 ${t('voice_synthesizing') || '语音合成中...'}\n\n${t('text')}: ${userContent}\n${t('voice')}: ${voiceId}\n${t('speech_speed')}: ${speechSpeed}x`,
      isLoading: true, 
      timestamp: Date.now() 
    }]);
    const assistantIndex = messages.length + 1;
    scrollToBottom();
    
    // 立即隐藏加载状态，用户可以做其他事情
    setMultimodalLoading(false);
    
    try {
      const result = await multimodalAPI.textToSpeech({
        model: selectedModel || 'doubao-bv001',
        input: userContent,
        voice: voiceId,
        speed: speechSpeed,
      });
      
      // 处理 blob 响应（新的二进制格式）
      if (result.blob) {
        const audioUrl = URL.createObjectURL(result.blob);
        setMultimodalResult({ type: 'audio', data: result });
        setMessages(prev => prev.map((m, i) => i === assistantIndex ? {
          role: 'assistant',
          content: `✅ ${t('voice_synthesis_success')}\n\n${t('text')}: ${userContent}\n${t('voice')}: ${voiceId}\n${t('speech_speed')}: ${speechSpeed}x`,
          audioUrl: audioUrl,
          isLoading: false,
          timestamp: Date.now(),
        } : m));
        scrollToBottom();
        return;
      }
      
      // 处理 JSON 响应（旧格式，兼容）
      const response = result;
      if (response.error) {
        setMessages(prev => prev.map((m, i) => i === assistantIndex ? {
          role: 'assistant',
          content: `❌ ${t('voice_synthesis_failed')}\n\n${t('text')}: ${userContent}\n${t('error_occurred')}: ${response.error.message || t('try_later')}`,
          isError: true,
          isLoading: false,
          timestamp: Date.now(),
        } : m));
        return;
      } else if (response.code && response.code !== 0) {
        setMessages(prev => prev.map((m, i) => i === assistantIndex ? {
          role: 'assistant',
          content: `❌ ${t('voice_synthesis_failed')}\n\n${t('text')}: ${userContent}\n${t('error_occurred')}: ${response.message || t('try_later')}`,
          isError: true,
          isLoading: false,
          timestamp: Date.now(),
        } : m));
        return;
      }
      
      // 将 base64 音频转换为 data URI 格式
      let audioDataUri = null;
      // 支持多种音频字段名
      const audioContent = response.audio_content || response.AudioContent || 
                          response.data?.audio_content || response.data?.AudioContent;
      if (audioContent) {
        audioDataUri = `data:audio/mpeg;base64,${audioContent}`;
      } else if (response.data?.audio_url) {
        audioDataUri = response.data.audio_url;
      }
      
      if (!audioDataUri) {
        setMessages(prev => prev.map((m, i) => i === assistantIndex ? {
          role: 'assistant',
          content: `❌ ${t('voice_synthesis_failed')}\n\n${t('text')}: ${userContent}\n${t('error_occurred')}: ${t('check_balance')}`,
          isError: true,
          isLoading: false,
          timestamp: Date.now(),
        } : m));
        return;
      }
      
      setMultimodalResult({ type: 'audio', data: response });
      setMessages(prev => prev.map((m, i) => i === assistantIndex ? {
        role: 'assistant',
        content: `✅ ${t('voice_synthesis_success')}\n\n${t('text')}: ${userContent}\n${t('voice')}: ${voiceId}\n${t('speech_speed')}: ${speechSpeed}x`,
        audioUrl: audioDataUri,
        isLoading: false,
        timestamp: Date.now(),
      } : m));
      scrollToBottom();
    } catch (error) {
      setMessages(prev => prev.map((m, i) => i === assistantIndex ? {
        role: 'assistant',
        content: `❌ ${t('voice_synthesis_failed')}\n\n${t('text')}: ${userContent}\n${t('error_occurred')}: ${error.message || t('try_later')}`,
        isError: true,
        isLoading: false,
        timestamp: Date.now(),
      } : m));
      scrollToBottom();
    }
    fetchBalance();
  };

  // 处理视频生成
  const handleGenerateVideo = async () => {
    if (!inputValue.trim()) {
      message.warning(t('enter_prompt') || '请输入提示词');
      return;
    }
    
    const userContent = inputValue;
    setInputValue(''); // 先清空输入框
    setMultimodalLoading(true);
    setMultimodalResult(null);
    
    // 先添加用户消息
    const userMsg = { role: 'user', content: userContent, timestamp: Date.now() };
    setMessages(prev => [...prev, userMsg]);
    
    // 添加"提交中"状态的消息
    setMessages(prev => [...prev, { 
      role: 'assistant', 
      content: `📹 ${t('video_submitting') || '视频生成任务提交中...'}\n\n${t('prompt')}: ${userContent}\n${t('model')}: ${selectedModel || 'wan2.7-t2v'}`,
      isLoading: true, 
      timestamp: Date.now() 
    }]);
    const assistantIndex = messages.length + 1;
    scrollToBottom();
    
    // 立即隐藏加载状态，用户可以做其他事情
    setMultimodalLoading(false);
    
    try {
      const reqBody = {
        model: selectedModel || 'wan2.7-t2v',
        prompt: userContent,
        duration: videoDuration,
        resolution: videoResolution,
        aspect_ratio: videoAspectRatio,
        watermark: videoWatermark,
      };
      if (!useRandomVideoSeed && videoSeed != null) reqBody.seed = parseInt(videoSeed);

      const response = await multimodalAPI.generateVideo(reqBody);
      
      // 处理响应 - 支持两种错误格式
      if (response.error) {
        setMessages(prev => prev.map((m, i) => i === assistantIndex ? {
          role: 'assistant',
          content: `❌ ${t('video_generation_failed')}\n\n${t('prompt')}: ${userContent}\n${t('error_occurred')}: ${response.error.message || t('try_later')}`,
          isError: true,
          isLoading: false,
          timestamp: Date.now(),
        } : m));
        return;
      } else if (response.code && response.code !== 0) {
        setMessages(prev => prev.map((m, i) => i === assistantIndex ? {
          role: 'assistant',
          content: `❌ ${t('video_generation_failed')}\n\n${t('prompt')}: ${userContent}\n${t('error_occurred')}: ${response.message || t('try_later')}`,
          isError: true,
          isLoading: false,
          timestamp: Date.now(),
        } : m));
        return;
      }
      
      setMultimodalResult({ type: 'video', data: response });
      setMessages(prev => prev.map((m, i) => i === assistantIndex ? {
        role: 'assistant',
        content: `✅ ${t('video_task_submitted')}\n\n${t('prompt')}: ${userContent}\n${t('model')}: ${selectedModel || 'wan2.7-t2v'}\n${t('task_id')}: ${response.task_id}\n\n${t('video_wait_message')}`,
        videoTaskId: response.task_id,
        isLoading: false,
        timestamp: Date.now(),
      } : m));
      scrollToBottom();
    } catch (error) {
      setMessages(prev => prev.map((m, i) => i === assistantIndex ? {
        role: 'assistant',
        content: `❌ ${t('video_generation_failed')}\n\n${t('prompt')}: ${userContent}\n${t('error_occurred')}: ${error.message || t('try_later')}`,
        isError: true,
        isLoading: false,
        timestamp: Date.now(),
      } : m));
      scrollToBottom();
    }
    fetchBalance();
  };

  // 处理 Embedding
  const handleCreateEmbedding = async () => {
    if (!inputValue.trim()) {
      message.warning(t('enter_text') || '请输入要嵌入的文本');
      return;
    }
    
    const userContent = inputValue;
    setInputValue(''); // 先清空输入框
    setMultimodalLoading(true);
    setMultimodalResult(null);
    
    // 先添加用户消息
    const userMsg = { role: 'user', content: userContent, timestamp: Date.now() };
    setMessages(prev => [...prev, userMsg]);
    
    // 添加"生成中"状态的消息
    setMessages(prev => [...prev, { 
      role: 'assistant', 
      content: `🔍 ${t('embedding_generating') || '向量生成中...'}\n\n${t('text')}: ${userContent}\n${t('model')}: ${selectedModel || 'embedding-3'}`,
      isLoading: true, 
      timestamp: Date.now() 
    }]);
    const assistantIndex = messages.length + 1;
    scrollToBottom();
    
    // 立即隐藏加载状态，用户可以做其他事情
    setMultimodalLoading(false);
    
    try {
      const response = await multimodalAPI.createEmbedding({
        model: selectedModel || 'embedding-3',
        input: userContent,
      });
      
      // 处理响应 - 支持两种错误格式
      if (response.error) {
        setMessages(prev => prev.map((m, i) => i === assistantIndex ? {
          role: 'assistant',
          content: `❌ ${t('embedding_generation_failed')}\n\n${t('text')}: ${userContent}\n${t('error_occurred')}: ${response.error.message || t('try_later')}`,
          isError: true,
          isLoading: false,
          timestamp: Date.now(),
        } : m));
        return;
      } else if (response.code && response.code !== 0) {
        setMessages(prev => prev.map((m, i) => i === assistantIndex ? {
          role: 'assistant',
          content: `❌ ${t('embedding_generation_failed')}\n\n${t('text')}: ${userContent}\n${t('error_occurred')}: ${response.message || t('try_later')}`,
          isError: true,
          isLoading: false,
          timestamp: Date.now(),
        } : m));
        return;
      }
      
      const embedding = response.data?.[0]?.embedding;
      setMultimodalResult({ type: 'embedding', data: response, embedding });
      setMessages(prev => prev.map((m, i) => i === assistantIndex ? {
        role: 'assistant',
        content: `✅ ${t('embedding_generation_success')}\n\n${t('text')}: ${userContent}\n${t('model')}: ${selectedModel || 'embedding-3'}\n${t('dimensions')}: ${embedding?.length || 0}\n\n${t('first_10_dims')}: [${embedding?.slice(0, 10).map(v => v.toFixed(4)).join(', ')}...]`,
        embedding: embedding,
        isLoading: false,
        timestamp: Date.now(),
      } : m));
      scrollToBottom();
    } catch (error) {
      setMessages(prev => prev.map((m, i) => i === assistantIndex ? {
        role: 'assistant',
        content: `❌ ${t('embedding_generation_failed')}\n\n${t('text')}: ${userContent}\n${t('error_occurred')}: ${error.message || t('try_later')}`,
        isError: true,
        isLoading: false,
        timestamp: Date.now(),
      } : m));
      scrollToBottom();
    }
    fetchBalance();
  };

  // 处理 Rerank（重排序）
  const handleCreateRerank = async () => {
    if (!inputValue.trim()) {
      message.warning(t('enter_rerank_content') || '请输入重排序的查询和文档');
      return;
    }

    const userContent = inputValue;
    setInputValue(''); // 先清空输入框
    setMultimodalLoading(true);
    setMultimodalResult(null);

    // 先添加用户消息
    const userMsg = { role: 'user', content: userContent, timestamp: Date.now() };
    setMessages(prev => [...prev, userMsg]);

    // 添加"处理中"状态的消息
    setMessages(prev => [...prev, { 
      role: 'assistant', 
      content: `🔄 ${t('rerank_processing') || '文档重排序中...'}\n\n${t('query')}: ${userContent}\n${t('model')}: ${selectedModel || 'rerank模型'}`,
      isLoading: true, 
      timestamp: Date.now() 
    }]);
    const assistantIndex = messages.length + 1;
    scrollToBottom();

    // 立即隐藏加载状态，用户可以做其他事情
    setMultimodalLoading(false);

    try {
      const response = await multimodalAPI.createRerank({
        model: selectedModel || 'rerank-model',
        query: userContent,
        documents: userContent.split('\n').filter(d => d.trim()),
      });

      // 处理响应
      if (response.error) {
        setMessages(prev => prev.map((m, i) => i === assistantIndex ? {
          role: 'assistant',
          content: `❌ ${t('rerank_failed') || '重排序失败'}\n\n${t('query')}: ${userContent}\n${t('error_occurred')}: ${response.error.message || t('try_later')}`,
          isError: true,
          isLoading: false,
          timestamp: Date.now(),
        } : m));
        return;
      } else if (response.code && response.code !== 0) {
        setMessages(prev => prev.map((m, i) => i === assistantIndex ? {
          role: 'assistant',
          content: `❌ ${t('rerank_failed') || '重排序失败'}\n\n${t('query')}: ${userContent}\n${t('error_occurred')}: ${response.message || t('try_later')}`,
          isError: true,
          isLoading: false,
          timestamp: Date.now(),
        } : m));
        return;
      }

      // 提取结果
      const results = response.results || response.data?.results || [];
      const resultsText = results.map((r, idx) => 
        `${idx + 1}. ${r.document || r.text || JSON.stringify(r)} (score: ${r.relevance_score || r.score || 0})`
      ).join('\n');

      setMultimodalResult({ type: 'rerank', data: response, results });
      setMessages(prev => prev.map((m, i) => i === assistantIndex ? {
        role: 'assistant',
        content: `✅ ${t('rerank_success') || '重排序完成'}\n\n${t('query')}: ${userContent}\n${t('model')}: ${selectedModel || 'rerank模型'}\n\n${t('sorted_results') || '排序结果'}:\n${resultsText}`,
        isLoading: false,
        timestamp: Date.now(),
      } : m));
      scrollToBottom();
    } catch (error) {
      setMessages(prev => prev.map((m, i) => i === assistantIndex ? {
        role: 'assistant',
        content: `❌ ${t('rerank_failed') || '重排序失败'}\n\n${t('query')}: ${userContent}\n${t('error_occurred')}: ${error.message || t('try_later')}`,
        isError: true,
        isLoading: false,
        timestamp: Date.now(),
      } : m));
      scrollToBottom();
    }
    fetchBalance();
  };

  // 处理 Transcription（语音识别）
  const handleCreateTranscription = async () => {
    if (!inputValue.trim()) {
      message.warning(t('enter_audio_url') || '请输入要转写的音频URL');
      return;
    }

    const userContent = inputValue;
    setInputValue(''); // 先清空输入框
    setMultimodalLoading(true);
    setMultimodalResult(null);

    // 先添加用户消息
    const userMsg = { role: 'user', content: userContent, timestamp: Date.now() };
    setMessages(prev => [...prev, userMsg]);

    // 添加"处理中"状态的消息
    setMessages(prev => [...prev, { 
      role: 'assistant', 
      content: `🎤 ${t('transcription_processing') || '语音转写中...'}\n\n${t('audio_url')}: ${userContent}\n${t('model')}: ${selectedModel || 'whisper模型'}`,
      isLoading: true, 
      timestamp: Date.now() 
    }]);
    const assistantIndex = messages.length + 1;
    scrollToBottom();

    // 立即隐藏加载状态，用户可以做其他事情
    setMultimodalLoading(false);

    try {
      const response = await multimodalAPI.createTranscription({
        model: selectedModel || 'whisper-1',
        audio_url: userContent,
      });

      // 处理响应
      if (response.error) {
        setMessages(prev => prev.map((m, i) => i === assistantIndex ? {
          role: 'assistant',
          content: `❌ ${t('transcription_failed') || '语音转写失败'}\n\n${t('audio_url')}: ${userContent}\n${t('error_occurred')}: ${response.error.message || t('try_later')}`,
          isError: true,
          isLoading: false,
          timestamp: Date.now(),
        } : m));
        return;
      } else if (response.code && response.code !== 0) {
        setMessages(prev => prev.map((m, i) => i === assistantIndex ? {
          role: 'assistant',
          content: `❌ ${t('transcription_failed') || '语音转写失败'}\n\n${t('audio_url')}: ${userContent}\n${t('error_occurred')}: ${response.message || t('try_later')}`,
          isError: true,
          isLoading: false,
          timestamp: Date.now(),
        } : m));
        return;
      }

      const text = response.text || response.data?.text || '';
      const language = response.language || response.data?.language;

      setMultimodalResult({ type: 'transcription', data: response, text });
      setMessages(prev => prev.map((m, i) => i === assistantIndex ? {
        role: 'assistant',
        content: `✅ ${t('transcription_success') || '语音转写完成'}\n\n${t('audio_url')}: ${userContent}\n${t('model')}: ${selectedModel || 'whisper模型'}\n${language ? `${t('detected_language') || '检测语言'}: ${language}\n` : ''}\n${t('transcription_result') || '转写结果'}:\n${text}`,
        isLoading: false,
        timestamp: Date.now(),
      } : m));
      scrollToBottom();
    } catch (error) {
      setMessages(prev => prev.map((m, i) => i === assistantIndex ? {
        role: 'assistant',
        content: `❌ ${t('transcription_failed') || '语音转写失败'}\n\n${t('audio_url')}: ${userContent}\n${t('error_occurred')}: ${error.message || t('try_later')}`,
        isError: true,
        isLoading: false,
        timestamp: Date.now(),
      } : m));
      scrollToBottom();
    }
    fetchBalance();
  };

  // 查询视频状态
  const handleCheckVideoStatus = async (taskId) => {
    try {
      const response = await multimodalAPI.getVideoStatus(taskId, selectedModel || 'video-01');
      
      // 检查响应结构 - 可能是错误响应 {code, message} 或成功响应 {status, video_url}
      if (response.code && response.code !== 0) {
        message.error(t('query_failed') + ': ' + (response.message || t('unknown_error')));
        return;
      }
      
      // 检查是否有 error 字段
      if (response.error) {
        message.error(response.error.message || t('query_failed'));
        return;
      }
      
      // internal-gateway 返回的是 { output: { task_status, video_url } }
      const output = response.output || response;
      const status = output.task_status || response.status || response.state || 'unknown';
      const videoUrl = output.video_url || response.video_url || response.videoUrl || '';
      
      if (status === 'completed' || status === 'success' || status === 'SUCCEEDED') {
        if (videoUrl) {
          setMessages(prev => {
            const updated = prev.map((m, i) =>
              m.videoTaskId === taskId
                ? { ...m, videoUrl: videoUrl, isLoading: false, content: m.content + `\n\n✅ ${t('video_generation_success')}` }
                : m
            );
            // 立即保存更新后的状态到历史对话
            saveCurrentConversation(updated);
            return updated;
          });
          message.success(t('video_generation_success'));
          setTimeout(() => scrollToBottom(), 100);
        } else {
          message.info(`${t('video_status')}: ${status}, Video URL not available`);
        }
      } else if (status === 'fail' || status === 'failed' || status === 'error' || status === 'FAILED' || status === 'CANCELED') {
        message.error(`${t('video_generation_failed')}: ${output.message || response.message || ''}`);
      } else if (status === 'pending' || status === 'processing') {
        message.info(`${t('video_status')}: ${status}, ${t('try_again_later')}`);
      } else {
        message.info(`${t('video_status')}: ${status}`);
      }
    } catch (error) {
      message.error(t('query_failed') + ': ' + (error.message || t('unknown_error')));
    }
  };

  const fetchApiKeys = async () => {
    try {
      const response = await apiKeyAPI.list();
      if (response.code === 0) {
        const keys = response.data.list || [];
        setApiKeys(keys);
        
        // 只在用户从未手动选择过key时才自动选择
        if (keys.length > 0) {
          const activeKey = keys.find(k => k.is_active);
          if (activeKey) {
            // 只有用户从未手动选择过key，才自动选择一个
            if (!userSelectedApiKeyRef.current) {
              setSelectedApiKey(activeKey);
            }
            setNoApiKey(false);
          } else if (keys.length > 0) {
            setNoApiKey(true);
          } else {
            setNoApiKey(true);
          }
        } else {
          setNoApiKey(true);
          setSelectedApiKey(null);
        }
      } else if (response.code === 401 || response.message?.includes('api_key')) {
        setNoApiKey(true);
      }
    } catch (error) {
      console.error('获取 API Keys 失败:', error);
      setNoApiKey(true);
    }
  };

  const getRetryDelay = (retryCount) => {
    if (retryCount < 5) return 100;
    if (retryCount < 7) return 300;
    if (retryCount < 9) return 500;
    return 800;
  };

  const executeStreamRequest = async (
    request,
    assistantMessageIndex,
    retryCount = 0,
    maxRetries = 10,
    previousContent = ''
  ) => {
    streamContentRef.current = previousContent;

    return new Promise((resolve, reject) => {
      const { abort, promise } = chatAPI.completionsStream(
        request,
        (chunk) => {
          if (chunk.choices && chunk.choices[0]) {
            const delta = chunk.choices[0].delta?.content || '';
            if (delta) {
              streamContentRef.current += delta;
              setMessages((prev) => {
                const newMessages = [...prev];
                if (newMessages[assistantMessageIndex]) {
                  newMessages[assistantMessageIndex] = {
                    ...newMessages[assistantMessageIndex],
                    content: streamContentRef.current,
                  };
                }
                return newMessages;
              });
              // 流式输出时立即滚动到底部
              scrollToBottom();
            }
          }
        },
        async (err) => {
          abortControllerRef.current = null;
          const currentContent = streamContentRef.current;
          const errorMsg = err?.message || '';
          const isAbortError = err?.name === 'AbortError' || errorMsg === 'canceled';
          
          // 检查是否是余额不足
          const isBalanceError = errorMsg.toLowerCase().includes('insufficient') || 
                                  errorMsg.toLowerCase().includes('balance') ||
                                  errorMsg.includes('余额') ||
                                  errorMsg.includes('insufficient_balance');
          
          // 检查是否是额度超限
          const isCreditError = errorMsg.includes('credit_exceeded') ||
                                errorMsg.includes('credit limit') ||
                                errorMsg.includes('额度');
          
          // 检查是否是没有 API Key
          const isNoApiKeyError = errorMsg.includes('no_api_key') ||
                                  errorMsg.includes('no_api_keys') ||
                                  errorMsg.includes('API Key');
          
          // 检查是否是超时
          const isTimeoutError = errorMsg.includes('timeout') || 
                                  errorMsg.includes('Timeout') ||
                                  errorMsg.includes('超时');

          if (retryCount < maxRetries && currentContent.length > 0 && !isBalanceError && !isCreditError && !isNoApiKeyError) {
            await new Promise((r) => setTimeout(r, getRetryDelay(retryCount)));
            const continuePrompt = t('continue_previous_content');
            const newMessages = [...request.messages, { role: 'user', content: continuePrompt }];

            try {
              await executeStreamRequest(
                { ...request, messages: newMessages },
                assistantMessageIndex,
                retryCount + 1,
                maxRetries,
                currentContent
              );
              resolve();
            } catch (retryErr) {
              reject(retryErr);
            }
          } else {
            setChatLoading(false);
            setIsStreaming(false);
            
            // 没有 API Key 的友好提示
            if (isNoApiKeyError) {
              const noApiKeyMsg = `🤖 **${t('no_api_key')}**

${t('no_api_key_msg')}

**Steps:**
1. ${t('no_api_key_step1')}
2. ${t('no_api_key_step2')}
3. ${t('no_api_key_step3')}
4. ${t('no_api_key_step4')}

💡 ${t('no_api_key_tip')}

[${t('create_api_key')}](/dashboard/apikeys)`;
              setMessages((prev) => {
                const newMessages = [...prev];
                if (newMessages[assistantMessageIndex]) {
                  newMessages[assistantMessageIndex] = {
                    ...newMessages[assistantMessageIndex],
                    content: noApiKeyMsg,
                  };
                }
                return newMessages;
              });
              setNoApiKey(true);
              // 不显示 toast 提示，让聊天消息引导用户
            } else if (isBalanceError) {
              // 余额不足的友好提示
              const balanceErrorMsg = `⚠️ **${t('balance_insufficient')}**\n\n${t('balance_insufficient_msg')}\n\n[${t('recharge_now')}](/dashboard/recharge)\n\n💡 ${t('balance_insufficient_tip')}`;
              setMessages((prev) => {
                const newMessages = [...prev];
                if (newMessages[assistantMessageIndex]) {
                  newMessages[assistantMessageIndex] = {
                    ...newMessages[assistantMessageIndex],
                    content: balanceErrorMsg,
                  };
                }
                return newMessages;
              });
              message.warning(t('balance_insufficient'));
            } else if (isCreditError) {
              // 额度超限的友好提示
              const creditErrorMsg = `⚠️ **${t('credit_limit_exceeded') || '额度超限'}**\n\n${t('credit_limit_exceeded_msg') || '当前 API Key 的额度已用完，请前往 API Keys 页面调整额度设置或创建新的 Key。'}\n\n[${t('manage_api_keys') || '管理 API Keys'}](/dashboard/apikeys)`;
              setMessages((prev) => {
                const newMessages = [...prev];
                if (newMessages[assistantMessageIndex]) {
                  newMessages[assistantMessageIndex] = {
                    ...newMessages[assistantMessageIndex],
                    content: creditErrorMsg,
                  };
                }
                return newMessages;
              });
              message.warning(t('credit_limit_exceeded') || 'API Key 额度已超限');
            } else if (isAbortError) {
              // 用户停止了请求
              const abortMsg = currentContent
                ? currentContent + `\n\n---\n⏹️ **请求已取消**（内容已截断）`
                : `⏹️ **请求已取消**`;
              setMessages((prev) => {
                const newMessages = [...prev];
                if (newMessages[assistantMessageIndex]) {
                  newMessages[assistantMessageIndex] = {
                    ...newMessages[assistantMessageIndex],
                    content: abortMsg,
                  };
                }
                return newMessages;
              });
              // 停止不需要显示 toast
            } else if (isTimeoutError) {
              // 超时
              const timeoutMsg = currentContent
                ? currentContent + `\n\n---\n⏱️ **请求超时**（内容已截断）`
                : `⏱️ **请求超时，请重试**`;
              setMessages((prev) => {
                const newMessages = [...prev];
                if (newMessages[assistantMessageIndex]) {
                  newMessages[assistantMessageIndex] = {
                    ...newMessages[assistantMessageIndex],
                    content: timeoutMsg,
                  };
                }
                return newMessages;
              });
              message.warning(t('request_timeout') || '请求超时');
            } else {
              const errorDisplayMsg = currentContent
                ? currentContent + `\n\n---\n⚠️ **请求失败：** ${err?.message || t('api_call_error')}`
                : `⚠️ **请求失败：** ${err?.message || t('api_call_error')}`;
              setMessages((prev) => {
                const newMessages = [...prev];
                if (newMessages[assistantMessageIndex]) {
                  newMessages[assistantMessageIndex] = {
                    ...newMessages[assistantMessageIndex],
                    content: errorDisplayMsg,
                  };
                }
                return newMessages;
              });
              if (retryCount >= maxRetries && currentContent.length > 0) {
                console.log('Retry limit reached with partial content');
              } else {
                message.error(err?.message || t('api_call_failed'));
              }
            }
            // 更新用户消息状态为失败
            setMessages(prev => prev.map((m, i) => 
              i === assistantMessageIndex - 1 && m.role === 'user' && m.status === 'sending'
                ? { ...m, status: 'failed' }
                : m
            ));
            reject(err);
          }
        },
        () => {
          setChatLoading(false);
          setIsStreaming(false);
          abortControllerRef.current = null;
          // 更新用户消息状态为已发送
          setMessages(prev => prev.map((m, i) => 
            i === assistantMessageIndex - 1 && m.role === 'user' && m.status === 'sending'
              ? { ...m, status: 'synced' }
              : m
          ));
          // 保存响应数据用于数据流面板展示
          setResponseData({
            content: streamContentRef.current,
            usage: null // usage 可能在 chunk 中，需要从流中捕获
          });
          fetchBalance();
          resolve();
        }
      );

      abortControllerRef.current = { abort };
      promise.catch(() => {});
    });
  };

  // 消息操作函数
  const handleEditMessage = (index) => {
    setEditingIndex(index);
    setEditContent(messages[index].content);
  };

  const handleSaveEdit = (index) => {
    if (!editContent.trim()) {
      setEditingIndex(null);
      setEditContent('');
      return;
    }

    // 显示确认对话框
    setPendingEditIndex(index);
    setShowEditConfirm(true);
  };

  const confirmEdit = async () => {
    const index = pendingEditIndex;
    setShowEditConfirm(false);
    setPendingEditIndex(null);

    // 更新用户消息
    const newMessages = [...messages];
    newMessages[index] = { ...newMessages[index], content: editContent.trim(), timestamp: Date.now() };
    setMessages(newMessages);
    setEditingIndex(null);
    setEditContent('');

    // 清空该消息之后的所有消息
    setMessages((prev) => prev.slice(0, index + 1));

    // 重新请求回复
    const currentModel = selectedModelRef.current;
    if (!currentModel) {
      message.warning(t('select_model_first') || 'Please select a model first');
      return;
    }

    // 构建API消息
    const apiMessages = newMessages
      .slice(0, index + 1)
      .filter((m) => m.content)
      .map((m) => ({ role: m.role, content: m.content }));

    setChatLoading(true);
    setIsStreaming(true);

    const assistantMessageIndex = index + 1;
    setMessages((prev) => [...prev, { role: 'assistant', content: '', timestamp: Date.now() }]);

    try {
      const request = {
        model: currentModel,
        messages: apiMessages,
        stream: true,
        // 合并用户训练参数（最高优先级）
        ...(userTrainedParams && {
          ...Object.fromEntries(
            Object.entries(userTrainedParams)
              .filter(([key]) => ['temperature', 'max_tokens', 'top_p', 'frequency_penalty', 'presence_penalty'].includes(key))
          )
        }),
      };

      await executeStreamRequest(request, assistantMessageIndex);
    } catch (error) {
      console.error('Edit request failed:', error);
    }
  };

  const cancelEdit = () => {
    setShowEditConfirm(false);
    setPendingEditIndex(null);
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setEditContent('');
  };

  const handleDeleteMessage = (index) => {
    const newMessages = messages.filter((_, i) => i !== index);
    setMessages(newMessages);
  };

  const handleRetry = async (index) => {
    if (isStreaming) {
      message.warning(t('streaming_warning'));
      return;
    }

    // 找到上一条用户消息
    let userMessageIndex = index - 1;
    while (userMessageIndex >= 0 && messages[userMessageIndex].role !== 'user') {
      userMessageIndex--;
    }

    if (userMessageIndex < 0) return;

    const userContent = messages[userMessageIndex].content;
    const currentModel = selectedModelRef.current;
    if (!currentModel) {
      message.warning(t('select_model_first') || 'Please select a model first');
      return;
    }

    // 删除从用户消息开始到当前消息
    const newMessages = messages.slice(0, userMessageIndex);
    setMessages(newMessages);

    // 构建API消息
    const apiMessages = newMessages
      .filter((m) => m.content)
      .map((m) => ({ role: m.role, content: m.content }));
    apiMessages.push({ role: 'user', content: userContent });

    // 添加用户消息和空的助手消息
    const userMessage = { role: 'user', content: userContent, timestamp: Date.now() };
    setMessages((prev) => [...prev, userMessage]);
    setChatLoading(true);
    setIsStreaming(true);

    const assistantMessageIndex = newMessages.length + 1;
    setMessages((prev) => [...prev, { role: 'assistant', content: '', timestamp: Date.now() }]);

    try {
      const request = {
        model: currentModel,
        messages: apiMessages,
        stream: true,
        key_id: selectedApiKey?.id,
        // 合并用户训练参数（最高优先级）
        ...(userTrainedParams && {
          ...Object.fromEntries(
            Object.entries(userTrainedParams)
              .filter(([key]) => ['temperature', 'max_tokens', 'top_p', 'frequency_penalty', 'presence_penalty'].includes(key))
          )
        }),
      };

      await executeStreamRequest(request, assistantMessageIndex);
    } catch (error) {
      console.error('Retry request failed:', error);
    }
  };

  const handleSend = async (presetPrompt = '') => {
    console.log('[DEBUG-SEND] 1. presetPrompt received:', presetPrompt);
    console.log('[DEBUG-SEND] 2. currentPresetId state:', currentPresetId);
    // 用户输入单独作为 user 消息，预设内容作为 system 消息
    const content = inputValue.trim();
    if (!content) {
      message.warning(t('enter_message', '请输入消息'));
      return;
    }
    if (isStreaming) {
      message.warning(t('streaming_warning'));
      return;
    }

    const currentModel = selectedModelRef.current;
    if (!currentModel) {
      message.warning(t('select_model_first') || 'Please select a model first');
      return;
    }

    // 确保有对话 ID，立即创建空对话（用于保存用户消息）
    if (!currentConversationId) {
      const provider = selectedProviderRef.current === 'all' 
        ? getProviderFromModel(selectedModelRef.current) 
        : selectedProviderRef.current;
      const newConv = {
        id: Date.now().toString(),
        title: t('new_chat'),
        messages: [],
        modeType: modeType,
        provider: provider,
        model: selectedModelRef.current,
        createdAt: Date.now(),
        updatedAt: Date.now(),
      };
      const newList = [newConv, ...conversations];
      setConversations(newList);
      setCurrentConversationId(newConv.id);
      saveConversations(newList);
    }

    const userMessage = { role: 'user', content, timestamp: Date.now(), status: 'sending' };

    // 判断当前模型是否匹配训练配置绑定的模型
    const trainedMatch = !userTrainedBoundModel || userTrainedBoundModel === currentModel;
    // 判断当前模型是否匹配预设绑定的模型
    const presetMatch = !presetBoundModel || presetBoundModel === currentModel;

    // 构建 API 消息：如果模型匹配预设绑定的模型，则应用预设 prompt 作为 system 消息
    const apiMessages = [];
    if (trainedMatch && presetPrompt && presetPrompt.trim()) {
      apiMessages.push({ role: 'system', content: presetPrompt.trim() });
    }
    messages
      .filter((m) => m.content)
      .forEach((m) => apiMessages.push({ role: m.role, content: m.content }));
    apiMessages.push({ role: 'user', content });

    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');
    setChatLoading(true);
    setIsStreaming(true);

    const assistantMessageIndex = messages.length + 1;
    setMessages((prev) => [...prev, { role: 'assistant', content: '', timestamp: Date.now() }]);

    // 只有模型匹配时才增加预设使用量
    if (currentPresetId && presetMatch) {
      presetAPI.incrementUsage(currentPresetId).catch(err => console.error('增加预设使用量失败:', err));
    }
    // 只有模型匹配时才增加训练配置使用量
    if (currentConfigId && trainedMatch) {
      userModelConfigAPI.incrementUsage(currentConfigId).catch(err => console.error('增加训练配置使用量失败:', err));
    }

    try {
      const request = {
        model: currentModel,
        messages: apiMessages,
        stream: true,
        key_id: selectedApiKey?.id, // 传递选中的 key_id（可能为空）
        // 只有模型匹配时才合并用户训练参数（最高优先级）
        ...(userTrainedParams && trainedMatch && {
          ...Object.fromEntries(
            Object.entries(userTrainedParams)
              .filter(([key]) => ['temperature', 'max_tokens', 'top_p', 'frequency_penalty', 'presence_penalty'].includes(key))
          )
        }),
      };

      // 保存请求数据用于数据流面板展示
      setRequestData(request);
      setResponseData(null); // 清空之前的响应数据

      await executeStreamRequest(request, assistantMessageIndex);
    } catch (error) {
      console.error('Stream request failed:', error);
    }
  };

  const handleAbort = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      setChatLoading(false);
      setIsStreaming(false);
      message.info(t('request_stopped') || 'Request stopped');
    }
  };

  const toggleFullscreen = () => {
    const chatContainer = document.querySelector('.chat-container');
    if (!chatContainer) return;

    if (!document.fullscreenElement) {
      chatContainer.requestFullscreen().then(() => {
        setIsFullscreen(true);
      }).catch(() => {
        setIsFullscreen(true);
      });
    } else {
      document.exitFullscreen().then(() => {
        setIsFullscreen(false);
      }).catch(() => {
        setIsFullscreen(false);
      });
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend(presetPrompt);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 120 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="dashboard-page-wrapper" style={{ display: 'flex', flexDirection: 'row', height: 'calc(100vh - 64px)', overflow: 'hidden' }}>
      {/* 右侧主内容区域 */}
      <div className="dashboard-page-container dashboard-content" style={{ flex: 1, overflow: 'hidden' }}>
        {/* 顶部统计卡片区域 */}
        <div className={`dashboard-stats ${userInfo?.is_affiliate ? 'has-affiliate' : ''}`}>
        <div className="stat-card-mini balance">
          <div className="stat-card-icon">
            <WalletOutlined />
          </div>
          <div className="stat-card-info">
            <span className="stat-card-label">{t('balance')}</span>
            <span className="stat-card-value">${Math.floor(balance * 100) / 100}</span>
          </div>
        </div>
        <div className="stat-card-mini models">
          <div className="stat-card-icon">
            <ThunderboltOutlined />
          </div>
          <div className="stat-card-info">
            <span className="stat-card-label">{t('available_models')}</span>
            <span className="stat-card-value">{models.length}+</span>
          </div>
        </div>
        <div className="stat-card-mini api">
          <div className="stat-card-icon">
            <ApiOutlined />
          </div>
          <div className="stat-card-info">
            <span className="stat-card-label">API Key</span>
            <span className="stat-card-value link" onClick={() => navigate('/dashboard/apikeys')}>
              {userInfo?.api_key ? (t('create_call') || '创建调用') : (t('create_now') || 'Create')} →
            </span>
          </div>
        </div>
        {userInfo?.is_affiliate && (
          <div className="stat-card-mini promo">
            <div className="stat-card-icon">
              <CrownOutlined />
            </div>
            <div className="stat-card-info">
              <span className="stat-card-label">{t('referral_bonus') || 'Referral Bonus'}</span>
              <span className="stat-card-value link" onClick={() => navigate('/dashboard/merchant')}>
                {t('up_to_20') || 'Up to 20%'} →
              </span>
            </div>
          </div>
        )}
      </div>

      {/* 主内容区域 */}
      <div className="dashboard-main" style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
        {/* 聊天区域 */}
        <div className="dashboard-chat" style={{ flex: 1, maxWidth: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <div className={`chat-container ${isFullscreen ? 'fullscreen' : ''}`}>
            {/* 聊天头部 */}
            <div className="chat-header">
              <div className="chat-header-left">
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  size="small"
                  onClick={createConversation}
                  style={{ borderRadius: 6 }}
                >
                  {t('new_chat') || 'New Chat'}
                </Button>

                {/* 对话历史列表切换按钮 */}
                <Tooltip title={showConversationList ? t('collapse_conversation_list') : t('expand_conversation_list')}>
                  <Button
                    type="text"
                    size="small"
                    icon={<MessageOutlined />}
                    onClick={() => setShowConversationList(!showConversationList)}
                    style={{
                      color: showConversationList ? '#1890ff' : '#666',
                      background: showConversationList ? '#e6f7ff' : 'transparent',
                      borderRadius: 6
                    }}
                  />
                </Tooltip>

                {/* API Key 选择器 */}
                {noApiKey ? (
                  <Button
                    size="small"
                    onClick={() => navigate('/dashboard/apikeys')}
                    style={{ 
                      borderRadius: 6,
                      backgroundColor: '#333',
                      color: '#fff',
                      border: 'none'
                    }}
                  >
                    <ApiOutlined /> {t('create_key_short')}
                  </Button>
                ) : (
                  <Select
                    value={selectedApiKey?.id}
                    onChange={(keyId) => {
                      const key = apiKeys.find(k => k.id === keyId);
                      setSelectedApiKey(key);
                      userSelectedApiKeyRef.current = true; // 标记用户已手动选择
                    }}
                    style={{ minWidth: 150 }}
                    size="small"
                    placeholder={t('select_key')}
                    getPopupContainer={() => document.querySelector('.chat-container') || document.body}
                  >
                    {apiKeys.filter(k => k.is_active).map((k) => (
                      <Select.Option key={k.id} value={k.id}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <span>{k.name}</span>
                          {k.credit_limit > 0 && (
                            <span style={{ fontSize: 10, color: '#999' }}>
                              ({(parseFloat(k.total_usage || 0)).toFixed(4)}/${k.credit_limit.toFixed(2)})
                            </span>
                          )}
                        </div>
                      </Select.Option>
                    ))}
                  </Select>
                )}
                
                {/* 模式选择 - 动态类型 */}
                <Select
                  value={modeType}
                  onChange={handleModeTypeChange}
                  style={{ minWidth: 160 }}
                  size="small"
                  getPopupContainer={() => document.querySelector('.chat-container') || document.body}
                >
                  {availableTypes.map((type) => {
                    const isDisabled = type === 'image' || type === 'video';
                    return (
                      <Select.Option key={type} value={type} disabled={isDisabled}
                        style={isDisabled ? { color: '#bfbfbf' } : {}}
                      >
                        <span>{getTypeIcon(type)} {getTypeDisplayName(type)} ({getTypeModelCount(type)})</span>
                      </Select.Option>
                    );
                  })}
                </Select>
                
                <Select
                  value={selectedProvider}
                  onChange={(value) => {
                    // 根据当前模式和选中的供应商过滤模型
                    let typeModels = models.filter(m => m.type === modeType);
                    
                    // 按供应商过滤
                    let filtered = typeModels;
                    if (value !== 'all') {
                      filtered = typeModels.filter(m => {
                        const providerName = typeof m.provider === 'object' ? m.provider?.display_name : m.provider;
                        return providerName === value;
                      });
                    }
                    
                    // 排序
                    let sorted = [...filtered];
                    if (value === 'all') {
                      sorted.sort((a, b) => (a.model_name || '').localeCompare(b.model_name || ''));
                    } else {
                      sorted.sort((a, b) => {
                        const vA = extractVersion(a.model_name);
                        const vB = extractVersion(b.model_name);
                        if (vB !== vA) return vB - vA;
                        return (a.model_name || '').localeCompare(b.model_name || '');
                      });
                    }
                    
                    // 更新供应商和模型
                    setSelectedProvider(value);
                    selectedProviderRef.current = value;
                    
                    // 自动选择排序后的第一个模型，如果没有则清空
                    if (sorted.length > 0) {
                      setSelectedModel(sorted[0].model_name);
                      selectedModelRef.current = sorted[0].model_name;
                    } else {
                      setSelectedModel('');
                      selectedModelRef.current = '';
                    }
                  }}
                  style={{ minWidth: 120 }}
                  size="small"
                  placeholder={t('select_provider') || 'Select Provider'}
                  getPopupContainer={() => document.querySelector('.chat-container') || document.body}
                >
                  <Select.Option key="all" value="all">
                    {t('all_providers')}
                  </Select.Option>
                  {filteredProviders.map((p) => (
                    <Select.Option key={p} value={p}>
                      {p}
                    </Select.Option>
                  ))}
                </Select>
                <Select
                  value={selectedModel}
                  onChange={(value) => {
                    // 切换模型时清空会话，开始新的对话
                    if (value !== selectedModel && messages.length > 0) {
                      setMessages([]);
                      setInputValue('');
                    }
                    // 如果当前有训练配置绑定的模型，且切换到的模型不匹配，清空训练配置的 prompt
                    if (userTrainedBoundModel && value !== userTrainedBoundModel) {
                      setPresetPrompt('');
                    }
                    // 如果当前有预设绑定的模型，且切换到的模型不匹配，清空预设的 prompt
                    if (presetBoundModel && value !== presetBoundModel) {
                      setPresetPrompt('');
                    }
                    setSelectedModel(value);
                    selectedModelRef.current = value;
                  }}
                  style={{ minWidth: 180 }}
                  size="small"
                  placeholder={filteredModels.length > 0 ? (t('select_model') || 'Select Model') : t('no_model_available') || 'No models available'}
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option.children || '').toLowerCase().includes(input.toLowerCase())
                  }
                  getPopupContainer={() => document.querySelector('.chat-container') || document.body}
                  notFoundContent={filteredModels.length === 0 ? (t('no_model_for_filter') || 'No models match current filter') : null}
                >
                  {filteredModels.map((m) => (
                    <Select.Option key={m.model_name} value={m.model_name}>
                      {m.display_name || m.model_name}
                    </Select.Option>
                  ))}
                </Select>
                {/* 用户训练配置指示器 */}
                {userTrainedConfig && (
                  <Tag
                    color={selectedModel === userTrainedBoundModel ? "purple" : "default"}
                    icon={<ThunderboltOutlined />}
                    style={{ marginLeft: 8, borderRadius: 12 }}
                  >
                    {userTrainedConfig.configName || t('trained_model', '训练参数')}
                    {userTrainedBoundModel && (
                      <span style={{ marginLeft: 4, opacity: 0.7 }}>
                        ({selectedModel === userTrainedBoundModel ? '✓' : '✗'} {userTrainedBoundModel})
                      </span>
                    )}
                  </Tag>
                )}
                {/* 预设指示器 */}
                {currentPresetName && (
                  <Tag
                    color={selectedModel === presetBoundModel ? "cyan" : "default"}
                    closable
                    onClose={(e) => { e.stopPropagation(); clearPreset(); }}
                    icon={<ThunderboltOutlined />}
                    style={{ marginLeft: 8, borderRadius: 12 }}
                  >
                    {currentPresetName}
                    {presetBoundModel && (
                      <span style={{ marginLeft: 4, opacity: 0.7 }}>
                        ({selectedModel === presetBoundModel ? '✓' : '✗'} {presetBoundModel})
                      </span>
                    )}
                  </Tag>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Tooltip title={t('data_flow', '数据流')} getPopupContainer={() => document.querySelector('.chat-container') || document.body}>
                  <Button
                    type="text"
                    size="small"
                    icon={<BranchesOutlined />}
                    onClick={() => setDataFlowVisible(true)}
                    style={{ color: '#722ed1' }}
                  />
                </Tooltip>
                <Tooltip title={isFullscreen ? (t('exit_fullscreen') || 'Exit Fullscreen') : (t('fullscreen') || 'Fullscreen')} getPopupContainer={() => document.querySelector('.chat-container') || document.body}>
                  <Button
                    type="text"
                    size="small"
                    icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
                    onClick={toggleFullscreen}
                    style={{ color: '#6b7280' }}
                  />
                </Tooltip>
              </div>
            </div>

            {/* 图片/视频模式提示：前往灵感创作页面获取更多参数 */}
            {(modeType === 'image' || modeType === 'video') && (
              <div style={{ padding: '0 12px', marginBottom: 4 }}>
                <Alert
                  type="info"
                  showIcon
                  icon={<BulbOutlined />}
                  message={
                    <span style={{ fontSize: 12 }}>
                      {modeType === 'image' ? t('use_creative_page_tip_image') : t('use_creative_page_tip_video')}
                    </span>
                  }
                  action={
                    <Button size="small" type="primary" ghost onClick={() => navigate('/dashboard/creative')}>
                      {t('go_creative')}
                    </Button>
                  }
                  style={{ borderRadius: 8 }}
                  closable
                />
              </div>
            )}

            {/* 消息区域 - 左侧对话列表 + 右侧消息内容 */}
            <div className="chat-messages" style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
              {/* 左侧对话列表（展开时显示） */}
              {showConversationList && (
                <div className={`conversation-list-container${conversationClosing ? ' closing' : ''}`} style={{
                  width: 280,
                  background: '#fff',
                  borderRadius: 12,
                  margin: 12,
                  marginRight: 24,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                  flexShrink: 0,
                  border: '1px solid #e8e8e8'
                }}>
                  {/* 列表头部 - 蓝色背景（缩小） */}
                  <div style={{
                    padding: '12px 16px',
                    background: 'linear-gradient(135deg, #1677ff 0%, #4096ff 100%)',
                    borderRadius: '12px 12px 0 0',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div style={{ fontWeight: 600, fontSize: 15, color: '#fff' }}>{t('history_conversations')}</div>
                    <Button
                      type="text"
                      size="small"
                      onClick={() => {
                        setConversationClosing(true);
                        setTimeout(() => {
                          setConversationClosing(false);
                          setShowConversationList(false);
                        }, 200);
                      }}
                      style={{ color: '#fff', fontSize: 13, height: 28, paddingLeft: 8, paddingRight: 8 }}
                      icon={<LeftOutlined style={{ fontSize: 12 }} />}
                    >
                      {t('collapse')}
                    </Button>
                  </div>
                  {/* 搜索框 - 白色区域 */}
                  <div style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0', background: '#fff' }}>
                    <Input
                      placeholder={t('search_conversation')}
                      prefix={<SearchOutlined style={{ color: '#999' }} />}
                      size="small"
                      style={{ borderRadius: 6 }}
                      value={conversationSearch}
                      onChange={(e) => setConversationSearch(e.target.value)}
                    />
                  </div>
                  {/* 对话列表 */}
                  <div style={{ flex: 1, overflow: 'auto' }} className="conversation-list-scroll">
                    {(conversationSearch ? conversations.filter(c => c.title.toLowerCase().includes(conversationSearch.toLowerCase())) : conversations).length > 0 ? 
                    (conversationSearch ? conversations.filter(c => c.title.toLowerCase().includes(conversationSearch.toLowerCase())) : conversations).map(conv => {
                      const handleMenuClick = (key) => {
                        if (key === 'pin') {
                          pinConversation(conv.id);
                        } else if (key === 'rename') {
                          startRename(conv);
                        } else if (key === 'delete') {
                          deleteConversation(conv.id);
                        }
                      };

                      const menu = (
                        <Menu onClick={({ key }) => handleMenuClick(key)}>
                          <Menu.Item key="pin" icon={<PushpinOutlined />}>
                            {t('pin')}
                          </Menu.Item>
                          <Menu.Item key="rename" icon={<EditOutlined />}>
                            {t('rename')}
                          </Menu.Item>
                          <Menu.Divider />
                          <Menu.Item key="delete" danger icon={<DeleteOutlined />}>
                            {t('delete')}
                          </Menu.Item>
                        </Menu>
                      );

                      return (
                        <div
                          key={conv.id}
                          className={`conversation-item ${currentConversationId === conv.id ? 'active' : ''}`}
                          style={{
                            padding: '14px 16px',
                            cursor: 'pointer',
                            borderBottom: '1px solid #f5f5f5',
                            background: currentConversationId === conv.id ? '#e6f4ff' : '#fff',
                            transition: 'all 0.2s',
                            borderLeft: currentConversationId === conv.id ? '3px solid #1677ff' : '3px solid transparent'
                          }}
                          onClick={() => {
                            if (renamingConvId !== conv.id && currentConversationId !== conv.id) {
                              switchConversation(conv.id);
                            }
                          }}
                          onMouseEnter={(e) => { if (currentConversationId !== conv.id) e.currentTarget.style.background = '#f0f5ff'; }}
                          onMouseLeave={(e) => { if (currentConversationId !== conv.id) e.currentTarget.style.background = '#fff'; }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div 
                              style={{ flex: 1, overflow: 'hidden', cursor: 'pointer' }}
                            >
                              {renamingConvId === conv.id ? (
                                <Input
                                  value={renameValue}
                                  onChange={(e) => setRenameValue(e.target.value)}
                                  onPressEnter={finishRename}
                                  onBlur={finishRename}
                                  autoFocus
                                  size="small"
                                  style={{ marginBottom: 4 }}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              ) : (
                                <div style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  marginBottom: 4
                                }}>
                                  <div style={{
                                    fontSize: 14,
                                    fontWeight: currentConversationId === conv.id ? 600 : 500,
                                    color: currentConversationId === conv.id ? '#1677ff' : '#333',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    flex: 1
                                  }}>
                                    {conv.title}
                                  </div>
                                  <Dropdown 
                                    overlay={menu} 
                                    trigger={['click']} 
                                    placement="bottomRight"
                                    getPopupContainer={(trigger) => trigger.parentElement}
                                  >
                                    <span 
                                      onClick={(e) => e.stopPropagation()} 
                                      style={{ cursor: 'pointer', padding: '4px', display: 'inline-flex', alignItems: 'center', marginLeft: 4 }}
                                    >
                                      <MoreOutlined style={{ color: '#999', fontSize: 16 }} />
                                    </span>
                                  </Dropdown>
                                </div>
                              )}
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div style={{ fontSize: 12, color: '#999', display: 'flex', gap: 8, alignItems: 'center' }}>
                                  <span>
                                    {new Date(conv.updatedAt).toLocaleString('zh-CN', {
                                      month: '2-digit',
                                      day: '2-digit',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })}
                                  </span>
                                  {conv.provider ? (
                                    <span style={{ color: '#1677ff', fontSize: 11 }}>
                                      {conv.provider}
                                    </span>
                                  ) : null}
                                </div>
                                <Button
                                  type="primary"
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (renamingConvId !== conv.id) {
                                      switchConversation(conv.id);
                                      setConversationClosing(true);
                                      setTimeout(() => {
                                        setConversationClosing(false);
                                        setShowConversationList(false);
                                      }, 200);
                                    }
                                  }}
                                  style={{ fontSize: 12, height: 22, paddingLeft: 8, paddingRight: 8 }}
                                >
                                  {t('enter_chat')}
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    }) : (
                      <div style={{ textAlign: 'center', padding: '40px 20px', color: '#999' }}>
                        <MessageOutlined style={{ fontSize: 40, marginBottom: 12, color: '#d9d9d9' }} />
                        <div style={{ fontSize: 14 }}>{conversationSearch ? t('no_matching_conversations') : t('no_conversations')}</div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* 右侧消息内容 */}
              <div style={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
                {messages.length === 0 && (
                <div className="chat-empty-state">
                  <div className="chat-empty-icon">
                    <MessageOutlined />
                  </div>
                  <div className="chat-empty-title">{t('start_chat')}</div>
                  <div className="chat-empty-desc">{t('chat_hint')}</div>
                  <div className="chat-suggestions">
                    <div className="suggestion-label">{t('try_asking') || 'Try asking:'}</div>
                    <div className="suggestion-tags">
                      {suggestionTags.map((text, i) => (
                        <span
                          key={i}
                          className="suggestion-tag"
                          onClick={() => setInputValue(text)}
                        >
                          {text}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              <div className="messages-scroll-container" style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
                {messages.map((msg, index) => (
                  <MessageItem
                    key={index}
                    msg={msg}
                    currentModel={selectedModel}
                    onEdit={() => handleEditMessage(index)}
                    onDelete={() => handleDeleteMessage(index)}
                    onRetry={() => handleRetry(index)}
                    isEditing={editingIndex === index}
                    editContent={editContent}
                    setEditContent={setEditContent}
                    onSaveEdit={() => handleSaveEdit(index)}
                    onCancelEdit={handleCancelEdit}
                    onCheckVideoStatus={handleCheckVideoStatus}
                  />
                ))}
                <div ref={messagesEndRef} />
              </div>
              </div>
            </div>

            {/* 输入区域 */}
            <div className="chat-input-area">
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                {/* 图片/视频/Embedding/语音输入 */}
                {modeType !== 'text' && (
                  <TextArea
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    placeholder={
                      modeType === 'image' ? t('image_prompt_placeholder') :
                      modeType === 'audio' ? t('voice_prompt_placeholder') :
                      modeType === 'video' ? t('video_prompt_placeholder') :
                      modeType === 'rerank' ? (t('rerank_prompt_placeholder') || '请输入查询文本，多个文档用换行分隔') :
                      modeType === 'transcription' ? (t('transcription_prompt_placeholder') || '请输入要转写的音频URL或音频文本') :
                      t('default_prompt_placeholder')
                    }
                    autoSize={{ minRows: 1, maxRows: 4 }}
                    disabled={multimodalLoading}
                    style={{
                      flex: 1,
                      minWidth: 200,
                      borderRadius: 6,
                      fontSize: 14,
                      resize: 'none',
                    }}
                  />
                )}
                
                {/* 聊天输入 */}
                {modeType === 'text' && (
                  <TextArea
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={t('input_message')}
                    autoSize={{ minRows: 1, maxRows: 4 }}
                    disabled={chatLoading}
                    style={{
                      flex: 1,
                      borderRadius: 6,
                      fontSize: 14,
                      resize: 'none',
                    }}
                  />
                )}
                
                {/* 图片模式快速参数 */}
                {modeType === 'image' && (
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span style={{ fontSize: 12, color: '#666' }}>{t('count')}:</span>
                    <InputNumber 
                      min={1} 
                      max={4} 
                      value={imageCount} 
                      onChange={setImageCount}
                      size="small"
                      style={{ width: 60 }}
                    />
                    <Select
                      value={imageSize}
                      onChange={setImageSize}
                      size="small"
                      style={{ width: 120 }}
                    >
                      <Select.Option value="1024x1024">1024x1024</Select.Option>
                      <Select.Option value="1024x1792">1024x1792</Select.Option>
                      <Select.Option value="1792x1024">1792x1024</Select.Option>
                    </Select>
                  </div>
                )}
                
                {modeType === 'speech' && (
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span style={{ fontSize: 12, color: '#666' }}>{t('voice_type')}:</span>
                    <Select
                      value={voiceId}
                      onChange={setVoiceId}
                      size="small"
                      style={{ width: 140 }}
                    >
                      <Select.Option value="BV001">{t('doubao_female') || '豆包-女声'}</Select.Option>
                      <Select.Option value="BV002">{t('doubao_male') || '豆包-男声'}</Select.Option>
                    </Select>
                    <span style={{ fontSize: 12, color: '#666' }}>{t('speech_speed') || '语速'}:</span>
                    <Slider
                      min={0.5}
                      max={2}
                      step={0.1}
                      value={speechSpeed}
                      onChange={setSpeechSpeed}
                      style={{ width: 80 }}
                      size="small"
                    />
                    <span style={{ fontSize: 12 }}>{speechSpeed}x</span>
                  </div>
                )}
                
                {modeType === 'video' && (
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span style={{ fontSize: 12, color: '#666' }}>{t('duration')}:</span>
                    <Slider
                      min={2}
                      max={15}
                      value={videoDuration}
                      onChange={setVideoDuration}
                      style={{ width: 100 }}
                      size="small"
                    />
                    <span style={{ fontSize: 12 }}>{videoDuration}{t('seconds')}</span>
                  </div>
                )}
                
                {/* 发送按钮 */}
                {modeType === 'text' ? (
                  isStreaming ? (
                    <Button
                      type="primary"
                      onClick={handleAbort}
                      style={{
                        height: 'auto',
                        minHeight: 40,
                        padding: '0 20px',
                        borderRadius: 6,
                      }}
                    >
                      <span style={{ display: 'inline-block', width: 8, height: 8, background: '#fff', marginRight: 8 }} />
                      {t('stop') || 'Stop'}
                    </Button>
                  ) : (
                    <Button
                      type="primary"
                      icon={<SendOutlined />}
                      onClick={() => handleSend(presetPrompt)}
                      disabled={chatLoading}
                      style={{
                        height: 'auto',
                        minHeight: 40,
                        padding: '0 20px',
                        borderRadius: 6,
                      }}
                    >
                      {t('send')}
                    </Button>
                  )
                ) : (
                  <Button
                    type="primary"
                    icon={multimodalLoading ? <LoadingOutlined /> : <SendOutlined />}
                    onClick={() => {
                      switch (modeType) {
                        case 'image': handleGenerateImage(); break;
                        case 'speech': handleTextToSpeech(); break;
                        case 'video': handleGenerateVideo(); break;
                        case 'embeddings': handleCreateEmbedding(); break;
                        case 'rerank': handleCreateRerank(); break;
                        case 'transcription': handleCreateTranscription(); break;
                      }
                    }}
                    disabled={multimodalLoading}
                    style={{
                      height: 'auto',
                      minHeight: 40,
                      padding: '0 20px',
                      borderRadius: 6,
                    }}
                  >
                    {multimodalLoading ? '处理中...' : (
                      modeType === 'image' ? t('generate_image') :
                      modeType === 'speech' ? t('generate_voice') :
                      modeType === 'video' ? t('generate_video') :
                      modeType === 'embeddings' ? t('generate_embedding') :
                      modeType === 'rerank' ? (t('rerank') || '重排序') :
                      modeType === 'transcription' ? (t('transcribe') || '语音转写') : t('send')
                    )}
                  </Button>
                )}
              </div>

              {/* 图片模式高级参数 */}
              {modeType === 'image' && (
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginTop: 8, paddingTop: 6, borderTop: '1px solid #f0f0f0' }}>
                  <span style={{ fontSize: 12, color: '#999' }}>{t('advanced_params')}:</span>
                  <span style={{ fontSize: 12, color: '#666' }}>{t('guidance_scale')}:</span>
                  <Slider
                    min={1}
                    max={20}
                    step={0.5}
                    value={imageGuidance}
                    onChange={setImageGuidance}
                    style={{ width: 80 }}
                    size="small"
                  />
                  <span style={{ fontSize: 12 }}>{imageGuidance}</span>
                  <Divider type="vertical" style={{ margin: '0 4px' }} />
                  <Switch
                    checkedChildren={t('random')}
                    unCheckedChildren=""
                    checked={useRandomImageSeed}
                    onChange={setUseRandomImageSeed}
                    size="small"
                  />
                  {!useRandomImageSeed && (
                    <>
                      <span style={{ fontSize: 12, color: '#666' }}>{t('custom_seed')}:</span>
                      <InputNumber
                        min={0}
                        max={99999999}
                        value={imageSeed}
                        onChange={setImageSeed}
                        size="small"
                        style={{ width: 80 }}
                        placeholder={t('random')}
                      />
                    </>
                  )}
                </div>
              )}

              {/* 视频模式高级参数 */}
              {modeType === 'video' && (
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginTop: 8, paddingTop: 6, borderTop: '1px solid #f0f0f0' }}>
                  <span style={{ fontSize: 12, color: '#999' }}>{t('advanced_params')}:</span>
                  <span style={{ fontSize: 12, color: '#666' }}>{t('resolution')}:</span>
                  <Radio.Group value={videoResolution} onChange={e => setVideoResolution(e.target.value)} size="small" optionType="button" buttonStyle="solid">
                    <Radio.Button value="480p" style={{ fontSize: 11, padding: '0 8px' }}>480p</Radio.Button>
                    <Radio.Button value="720p" style={{ fontSize: 11, padding: '0 8px' }}>720p</Radio.Button>
                    <Radio.Button value="1080p" style={{ fontSize: 11, padding: '0 8px' }}>1080p</Radio.Button>
                  </Radio.Group>
                  <Divider type="vertical" style={{ margin: '0 4px' }} />
                  <span style={{ fontSize: 12, color: '#666' }}>{t('aspect_ratio')}:</span>
                  <Select value={videoAspectRatio} onChange={setVideoAspectRatio} size="small" style={{ width: 80 }}>
                    <Select.Option value="16:9">16:9</Select.Option>
                    <Select.Option value="9:16">9:16</Select.Option>
                    <Select.Option value="1:1">1:1</Select.Option>
                    <Select.Option value="4:3">4:3</Select.Option>
                    <Select.Option value="3:4">3:4</Select.Option>
                  </Select>
                  <Divider type="vertical" style={{ margin: '0 4px' }} />
                  <Switch
                    checkedChildren={t('random')}
                    unCheckedChildren=""
                    checked={useRandomVideoSeed}
                    onChange={setUseRandomVideoSeed}
                    size="small"
                  />
                  {!useRandomVideoSeed && (
                    <>
                      <span style={{ fontSize: 12, color: '#666' }}>{t('custom_seed')}:</span>
                      <InputNumber
                        min={0}
                        max={99999999}
                        value={videoSeed}
                        onChange={setVideoSeed}
                        size="small"
                        style={{ width: 80 }}
                        placeholder={t('random')}
                      />
                    </>
                  )}
                  <Divider type="vertical" style={{ margin: '0 4px' }} />
                  <span style={{ fontSize: 12, color: '#666' }}>{t('watermark_label')}:</span>
                  <Switch checked={videoWatermark} onChange={setVideoWatermark} size="small" />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      </div>

      {/* 编辑确认对话框 */}
      <Modal
        title={t('confirm_edit') || 'Confirm Edit'}
        open={showEditConfirm}
        onOk={confirmEdit}
        onCancel={cancelEdit}
        okText={t('confirm')}
        cancelText={t('cancel')}
        centered
      >
        <p>{t('edit_confirm_msg') || 'After saving, all messages after this one will be deleted and a new AI response will be generated. Continue?'}</p>
      </Modal>

      {/* 用户引导弹窗 - 水平步骤设计 */}
      <Modal
        open={showGuideModal}
        onCancel={() => {
          localStorage.setItem('has_seen_user_guide', 'true');
          setShowGuideModal(false);
        }}
        footer={null}
        width={900}
        centered
        title={
          <span style={{ fontSize: 16, fontWeight: 600 }}>
            {t('user_guide')}
          </span>
        }
      >
        <div style={{ padding: '16px 0 24px' }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center',
            alignItems: 'flex-start',
            gap: 0,
            marginBottom: 32
          }}>
            {/* 步骤1 */}
            <div 
              style={{ 
                flex: 1, 
                textAlign: 'center', 
                padding: '0 12px',
                cursor: 'pointer',
                maxWidth: 200
              }}
              onClick={() => {
                localStorage.setItem('has_seen_user_guide', 'true');
                setShowGuideModal(false);
                navigate('/dashboard/models');
              }}
            >
              <div style={{ 
                width: 48, 
                height: 48, 
                borderRadius: '50%', 
                background: '#1677ff', 
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 600,
                fontSize: 18,
                margin: '0 auto 12px',
                boxShadow: '0 2px 8px rgba(22, 119, 255, 0.3)'
              }}>
                1
              </div>
              <div style={{ fontWeight: 600, fontSize: 14, color: '#333', marginBottom: 6 }}>
                {t('step1_title')}
              </div>
              <div style={{ fontSize: 12, color: '#666', lineHeight: 1.5 }}>
                {t('step1_desc')}
              </div>
            </div>

            {/* 连接线1 */}
            <div style={{ 
              width: 60, 
              height: 2, 
              background: '#e8e8e8',
              marginTop: 24,
              flexShrink: 0
            }} />

            {/* 步骤2 */}
            <div 
              style={{ 
                flex: 1, 
                textAlign: 'center', 
                padding: '0 12px',
                cursor: 'pointer',
                maxWidth: 200
              }}
              onClick={() => {
                localStorage.setItem('has_seen_user_guide', 'true');
                setShowGuideModal(false);
                navigate('/dashboard/model-training');
              }}
            >
              <div style={{ 
                width: 48, 
                height: 48, 
                borderRadius: '50%', 
                background: '#1677ff', 
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 600,
                fontSize: 18,
                margin: '0 auto 12px',
                boxShadow: '0 2px 8px rgba(22, 119, 255, 0.3)'
              }}>
                2
              </div>
              <div style={{ fontWeight: 600, fontSize: 14, color: '#333', marginBottom: 6 }}>
                {t('step2_title')}
              </div>
              <div style={{ fontSize: 12, color: '#666', lineHeight: 1.5 }}>
                {t('step2_desc')}
              </div>
            </div>

            {/* 连接线2 */}
            <div style={{ 
              width: 60, 
              height: 2, 
              background: '#e8e8e8',
              marginTop: 24,
              flexShrink: 0
            }} />

            {/* 步骤3 */}
            <div 
              style={{ 
                flex: 1, 
                textAlign: 'center', 
                padding: '0 12px',
                cursor: 'pointer',
                maxWidth: 200
              }}
              onClick={() => {
                localStorage.setItem('has_seen_user_guide', 'true');
                setShowGuideModal(false);
              }}
            >
              <div style={{ 
                width: 48, 
                height: 48, 
                borderRadius: '50%', 
                background: '#1677ff', 
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 600,
                fontSize: 18,
                margin: '0 auto 12px',
                boxShadow: '0 2px 8px rgba(22, 119, 255, 0.3)'
              }}>
                3
              </div>
              <div style={{ fontWeight: 600, fontSize: 14, color: '#333', marginBottom: 6 }}>
                {t('step3_title')}
              </div>
              <div style={{ fontSize: 12, color: '#666', lineHeight: 1.5 }}>
                {t('step3_desc')}
              </div>
            </div>

            {/* 连接线3 */}
            <div style={{ 
              width: 60, 
              height: 2, 
              background: '#e8e8e8',
              marginTop: 24,
              flexShrink: 0
            }} />

            {/* 步骤4 */}
            <div 
              style={{ 
                flex: 1, 
                textAlign: 'center', 
                padding: '0 12px',
                cursor: 'pointer',
                maxWidth: 200
              }}
              onClick={() => {
                localStorage.setItem('has_seen_user_guide', 'true');
                setShowGuideModal(false);
                navigate('/dashboard/apikeys');
              }}
            >
              <div style={{ 
                width: 48, 
                height: 48, 
                borderRadius: '50%', 
                background: '#1677ff', 
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 600,
                fontSize: 18,
                margin: '0 auto 12px',
                boxShadow: '0 2px 8px rgba(22, 119, 255, 0.3)'
              }}>
                4
              </div>
              <div style={{ fontWeight: 600, fontSize: 14, color: '#333', marginBottom: 6 }}>
                {t('step4_title')}
              </div>
              <div style={{ fontSize: 12, color: '#666', lineHeight: 1.5 }}>
                {t('step4_desc')}
              </div>
            </div>
          </div>

          <div style={{ textAlign: 'center', marginTop: 8 }}>
            <Button 
              type="primary"
              onClick={() => {
                localStorage.setItem('has_seen_user_guide', 'true');
                setShowGuideModal(false);
              }}
              style={{ minWidth: 120, height: 36, borderRadius: 4 }}
            >
              {t('got_it')}
            </Button>
            <div style={{ marginTop: 12, fontSize: 12, color: '#999' }}>
              {t('guide_tip')}
            </div>
          </div>
        </div>
      </Modal>

      {/* 数据流侧边栏 */}
      <div 
        style={{
          position: 'fixed',
          top: 0,
          right: dataFlowVisible ? 0 : '-450px',
          width: 450,
          height: '100vh',
          background: '#fff',
          boxShadow: dataFlowVisible ? '-2px 0 8px rgba(0,0,0,0.15)' : 'none',
          transition: 'right 0.3s ease',
          zIndex: 1000,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* 侧边栏头部 */}
        <div style={{ 
          padding: '12px 16px', 
          borderBottom: '1px solid #f0f0f0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: '#722ed1',
          color: '#fff'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <BranchesOutlined />
            <span style={{ fontWeight: 500 }}>{t('data_flow', '数据流')}</span>
          </div>
          <Button 
            type="text" 
            icon={<CloseCircleOutlined />} 
            onClick={() => setDataFlowVisible(false)}
            style={{ color: '#fff' }}
          />
        </div>

        {/* 侧边栏内容 */}
        <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
          {/* 请求配置 */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 6, 
              marginBottom: 10,
              fontSize: 13,
              fontWeight: 500,
              color: '#722ed1'
            }}>
              <ArrowRightOutlined />
              <span>{t('request_config', '请求配置')}</span>
            </div>
            <div style={{ padding: 12, background: '#fafafa', borderRadius: 8, fontSize: 12 }}>
              {/* 模型 */}
              <div style={{ marginBottom: 10 }}>
                <Text type="secondary" style={{ fontSize: 11 }}>{t('model', '模型')}</Text>
                <div style={{ marginTop: 4 }}>
                  <Tag color="blue">{selectedModel || '-'}</Tag>
                </div>
              </div>
              
              {/* 训练配置 */}
              <div style={{ marginBottom: 10 }}>
                <Text type="secondary" style={{ fontSize: 11 }}>{t('trained_config', '训练配置')}</Text>
                <div style={{ marginTop: 4 }}>
                  {userTrainedConfig ? (
                    <Tag color="purple">{userTrainedConfig.configName || userTrainedConfig.name}</Tag>
                  ) : (
                    <Text type="secondary" style={{ fontSize: 11 }}>{t('not_used', '未使用')}</Text>
                  )}
                </div>
              </div>

              {/* 预设 */}
              <div style={{ marginBottom: 10 }}>
                <Text type="secondary" style={{ fontSize: 11 }}>{t('preset', '预设')}</Text>
                <div style={{ marginTop: 4 }}>
                  {currentPresetName ? (
                    <Tag color="cyan">{currentPresetName}</Tag>
                  ) : (
                    <Text type="secondary" style={{ fontSize: 11 }}>{t('not_used', '未使用')}</Text>
                  )}
                </div>
              </div>
              
              {/* 实际参数 */}
              <div>
                <Text type="secondary" style={{ fontSize: 11 }}>{t('actual_params', '实际参数')}</Text>
                <div style={{ 
                  marginTop: 4, 
                  padding: 8, 
                  background: '#fff', 
                  borderRadius: 4, 
                  fontSize: 11, 
                  fontFamily: 'monospace'
                }}>
                  <div><span style={{ color: '#666' }}>temperature:</span> {userTrainedParams?.temperature ?? '-'}</div>
                  <div><span style={{ color: '#666' }}>max_tokens:</span> {userTrainedParams?.max_tokens ?? '-'}</div>
                  <div><span style={{ color: '#666' }}>top_p:</span> {userTrainedParams?.top_p ?? '-'}</div>
                  <div><span style={{ color: '#722ed1' }}>frequency_penalty:</span> {userTrainedParams?.frequency_penalty ?? '-'}</div>
                  <div><span style={{ color: '#722ed1' }}>presence_penalty:</span> {userTrainedParams?.presence_penalty ?? '-'}</div>
                </div>
              </div>
            </div>
          </div>

          {/* 实际请求体 */}
          {requestData && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 6, 
                marginBottom: 10,
                fontSize: 13,
                fontWeight: 500,
                color: '#722ed1'
              }}>
                <ArrowRightOutlined />
                <span>{t('actual_request_body', '实际请求体')}</span>
              </div>
              <pre style={{ 
                padding: 12, 
                background: '#1e1e1e', 
                borderRadius: 8, 
                fontSize: 10, 
                fontFamily: 'monospace',
                color: '#d4d4d4',
                maxHeight: 200,
                overflow: 'auto',
                whiteSpace: 'pre-wrap'
              }}>
                {JSON.stringify(requestData, null, 2)}
              </pre>
            </div>
          )}

          {/* 响应数据 */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 6, 
              marginBottom: 10,
              fontSize: 13,
              fontWeight: 500,
              color: '#722ed1'
            }}>
              <ArrowRightOutlined />
              <span>{t('response_data', '响应数据')}</span>
            </div>
            {responseData ? (
              <div style={{ padding: 12, background: '#fafafa', borderRadius: 8, fontSize: 12 }}>
                <div>
                  <Text type="secondary" style={{ fontSize: 11 }}>{t('response_content', '响应内容')}</Text>
                  <div style={{ 
                    marginTop: 4, 
                    padding: 12, 
                    background: '#fff', 
                    borderRadius: 4, 
                    maxHeight: 200, 
                    overflow: 'auto', 
                    whiteSpace: 'pre-wrap', 
                    wordBreak: 'break-word',
                    fontSize: 11
                  }}>
                    {responseData.content || t('empty_response', '(空响应)')}
                  </div>
                </div>
              </div>
            ) : (
              <div style={{ padding: 24, background: '#fafafa', borderRadius: 8, color: '#999', textAlign: 'center' }}>
                <MessageOutlined style={{ fontSize: 24, marginBottom: 8 }} />
                <div>{t('no_response_yet', '暂无响应数据，请发送消息后查看')}</div>
              </div>
            )}
          </div>

          {/* 说明 */}
          <div style={{ padding: 12, background: '#e6f7ff', borderRadius: 8, fontSize: 11 }}>
            <Text type="secondary">
              💡 {t('data_flow_tip', '此面板显示实际发送的请求参数和响应数据，方便调试和验证是否使用了正确的训练参数。')}
            </Text>
          </div>
        </div>
      </div>

      {/* 遮罩层 */}
      {dataFlowVisible && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.45)',
            zIndex: 999
          }}
          onClick={() => setDataFlowVisible(false)}
        />
      )}
    </div>
  );
};

export default Dashboard;
